import type { CareSidebarSubBucket, OrderRecord } from '@/api/hmCareOrderTypes'
import {
  CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE,
  CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
  CARE_SERVICE_STATUS_FU_ASSIGNED,
  CARE_SERVICE_STATUS_FU_COMPLETED,
  CARE_SERVICE_STATUS_PENDING_SCHEDULE,
  classifyCareSidebarSubBucket,
  mainServiceStatusIsAssignedOrBeyond,
  careServiceStatusCodeToDisplayLabel,
} from '@/api/hmCareOrderTypes'
import request from '@/api/request'
import { clampPageSize } from '@/utils/fieldLimitValidation'
import { formatOrderScheduleLineDisplay, formatServiceTimeRangeLine } from '@/utils/orderScheduleDisplay'

/** 与 care-order meta `search_types[].value` 对齐，用于列表查询 */
export type OrderSearchField =
  | 'order_id'
  | 'home_id'
  | 'user_id'
  | 'contact_phone_number'
  | 'address'
  | 'subscription_id'
  | 'report_id'
  | 'follow_up_id'
  | 'service_address'
  | 'contact_name'

export interface CareMetaOption<T = string> {
  value: T
  label: string
  staff_no?: string
  role?: string
}

export interface CareTemplateMeta {
  id: number
  value: number
  label: string
  template_key: string
  period_type: string
  month: number | null
  start_month: number | null
  end_month: number | null
  estimated_minutes: number
  /** meta 与模板库一致时优先用于 Schedule 标题括号工时 */
  estimated_hours?: number
}

export interface CareOrderMetaData {
  plans: CareMetaOption<string>[]
  plan_prices: Record<string, number>
  service_statuses: CareMetaOption<string>[]
  search_types: CareMetaOption<string>[]
  technicians: CareMetaOption<number>[]
  templates_by_plan: Record<string, CareTemplateMeta[]>
  tax_rate: number
  payment_method: string
}

function unwrapCareMeta(raw: unknown): CareOrderMetaData {
  const r = raw as { code?: number; msg?: string; data?: CareOrderMetaData }
  if (r?.code === 200 && r.data && typeof r.data === 'object') return r.data
  const msg = typeof r?.msg === 'string' ? r.msg : 'Failed to load care order metadata'
  throw new Error(msg)
}

/** POST `api/admin/care-order/meta`，需 header `token` */
export async function postCareOrderMeta(): Promise<CareOrderMetaData> {
  const raw = await request.post('/api/admin/care-order/meta', {})
  return unwrapCareMeta(raw)
}

export interface Technician {
  id: string
  name: string
}

export interface OrderServiceLineDTO {
  id?: string
  /** Follow-up 主键（`care-schedule/assign` 的 `target_id`，`target_type: followup`） */
  followUpPrimaryId?: number
  serviceName: string
  productCode: string
  scheduledAt: string | null
  address: string
  assignment: string
  technicianRef?: string
  price: string
  paymentMethod: string
  discount?: string
  canSchedule: boolean
  canReschedule: boolean
  canRefund: boolean
  canMessage: boolean
  canNote: boolean
}

export interface OrderCardDTO {
  /** 业务单号，与列表/详情 `order_id` 一致，用于展示与行操作 URL */
  id: string
  /** 订单主键 `id`，仅详情接口 POST body 使用；本地 MOCK 或未返回时可能为空 */
  orderPrimaryId?: number
  homeId: string
  status: string
  /** 列表接口 `service_status` 枚举（snake_case），用于侧栏分桶，与展示文案 `status` 解耦 */
  serviceStatusCode?: string
  /** 服务周期日（`service_date`，多为 YYYY-MM-DD），待排期时用于排程页限定可选日期 */
  serviceDate?: string
  placementDate: string
  notes?: string
  /** 订单级多个首选上门时间（Follow-up 行 Pending Reschedule + 悬停列表） */
  preferredTimes?: string[]
  services: OrderServiceLineDTO[]
}

function isEmptyCareOrderDisplayText(value: string | null | undefined): boolean {
  const s = (value ?? '').trim()
  return !s || s === '—'
}

function isPendingSchedulePlaceholderText(raw: string | undefined): boolean {
  const s = (raw ?? '').trim()
  if (!s) return false
  return (
    /\bpending\s+(schedule|reschedule)\b/i.test(s) ||
    /^follow-up-pending\s+schedule$/i.test(s) ||
    /^follow-up-pending\s+reschedule$/i.test(s)
  )
}

function mergeScheduledAtPreservingBooked(
  prev: OrderServiceLineDTO,
  next: OrderServiceLineDTO,
): string | null {
  const prevRaw = (prev.scheduledAt ?? '').trim()
  const nextRaw = (next.scheduledAt ?? '').trim()
  /** Unassign 后接口明确回到 Pending Schedule / Reschedule 占位：以后端为准 */
  if (nextRaw && isPendingSchedulePlaceholderText(nextRaw)) {
    return next.scheduledAt
  }
  const prevBooked = isBookedScheduleDisplayLabel(prevRaw)
  if (prevBooked && !nextRaw) return prev.scheduledAt
  return next.scheduledAt ?? prev.scheduledAt
}

function lineHasLocalAssignee(line: OrderServiceLineDTO): boolean {
  if (line.technicianRef?.trim()) return true
  const assign = (line.assignment ?? '').trim()
  return !!assign && !isPendingAssignmentLabel(assign)
}

/** Assign 后接口快照常仍带 Pending Assignment：勿覆盖本地已确认的检查员（仅当接口时段仍为已预约） */
function mergeAssignmentPreservingLocal(
  prev: OrderServiceLineDTO,
  next: OrderServiceLineDTO,
): string {
  const incoming = (next.assignment ?? '').trim()
  if (
    lineHasLocalAssignee(prev) &&
    isPendingAssignmentLabel(incoming) &&
    isBookedScheduleDisplayLabel(next.scheduledAt)
  ) {
    return prev.assignment ?? 'Pending Assignment'
  }
  if (isEmptyCareOrderDisplayText(next.assignment)) {
    return prev.assignment ?? 'Pending Assignment'
  }
  return next.assignment ?? prev.assignment ?? 'Pending Assignment'
}

function mergeTechnicianRefPreservingLocal(
  prev: OrderServiceLineDTO,
  next: OrderServiceLineDTO,
): string | undefined {
  if (
    lineHasLocalAssignee(prev) &&
    !next.technicianRef?.trim() &&
    isBookedScheduleDisplayLabel(next.scheduledAt)
  ) {
    return prev.technicianRef
  }
  return next.technicianRef ?? prev.technicianRef
}

/** 接口快照缺字段时保留列表已有展示（地址/价格/支付方式等），排程相关字段仍以后端为准 */
export function mergeCareOrderCardPreservingLocalFields(
  existing: OrderCardDTO,
  incoming: OrderCardDTO,
): OrderCardDTO {
  const listId = (existing.id ?? '').trim() || (incoming.id ?? '').trim()
  const mergedServices: OrderServiceLineDTO[] = []
  const maxLen = Math.max(existing.services.length, incoming.services.length)
  for (let i = 0; i < maxLen; i++) {
    const prev = existing.services[i]
    const next = incoming.services[i]
    if (!prev) {
      if (next) mergedServices.push(next)
      continue
    }
    if (!next) {
      mergedServices.push(prev)
      continue
    }
    mergedServices.push({
      ...prev,
      ...next,
      serviceName: isEmptyCareOrderDisplayText(next.serviceName) ? prev.serviceName : next.serviceName,
      productCode: isEmptyCareOrderDisplayText(next.productCode) ? prev.productCode : next.productCode,
      address: isEmptyCareOrderDisplayText(next.address) ? prev.address : next.address,
      price: isEmptyCareOrderDisplayText(next.price) ? prev.price : next.price,
      paymentMethod: isEmptyCareOrderDisplayText(next.paymentMethod)
        ? prev.paymentMethod
        : next.paymentMethod,
      discount: next.discount ?? prev.discount,
      scheduledAt: mergeScheduledAtPreservingBooked(prev, next),
      assignment: mergeAssignmentPreservingLocal(prev, next),
      technicianRef: mergeTechnicianRefPreservingLocal(prev, next),
      canSchedule: next.canSchedule ?? prev.canSchedule,
      canReschedule: next.canReschedule ?? prev.canReschedule,
      canRefund: next.canRefund ?? prev.canRefund,
      canMessage: next.canMessage ?? prev.canMessage,
      canNote: next.canNote ?? prev.canNote,
      followUpPrimaryId: next.followUpPrimaryId ?? prev.followUpPrimaryId,
      id: next.id ?? prev.id,
    })
  }

  const merged: OrderCardDTO = {
    ...existing,
    ...incoming,
    id: listId || incoming.id,
    orderPrimaryId: mergeOrderPrimaryIdField(existing, incoming),
    homeId: isEmptyCareOrderDisplayText(incoming.homeId) ? existing.homeId : incoming.homeId,
    status: isEmptyCareOrderDisplayText(incoming.status) ? existing.status : incoming.status,
    serviceStatusCode: incoming.serviceStatusCode ?? existing.serviceStatusCode,
    serviceDate: incoming.serviceDate ?? existing.serviceDate,
    placementDate: isEmptyCareOrderDisplayText(incoming.placementDate)
      ? existing.placementDate
      : incoming.placementDate,
    notes: incoming.notes ?? existing.notes,
    preferredTimes: incoming.preferredTimes?.length ? incoming.preferredTimes : existing.preferredTimes,
    services: mergedServices,
  }
  return merged
}

export interface OrderListQuery {
  page: number
  pageSize: number
  searchField: OrderSearchField
  searchQuery?: string
  serviceDate?: string
  followUpDate?: string
  serviceStatus?: string
  technicianId?: string
  inspectorId?: string
}

/** POST /api/admin/care-order/detail 的 timeline 单条 */
export interface CareOrderTimelineEvent {
  id?: number
  event_type?: string
  target_type?: string
  target_id?: number
  title?: string
  occurred_at?: string
  event_at?: string
  event_time?: string
  created_at?: string
  at?: string
  actor_name?: string
  actor?: string | Record<string, unknown> | null
  actor_text?: string
  by?: string
  by_name?: string
  operator_name?: string
  operator?: string
  created_by_name?: string
  created_by?: string
  user_name?: string
  [key: string]: unknown
}

export interface CareOrderAvailableActions {
  order?: Record<string, number | boolean | string | undefined>
  followups?: Array<Record<string, number | boolean | string | undefined>>
  followup?: Record<string, number | boolean | string | undefined>
  [key: string]: unknown
}

export interface CareOrderCareTemplate {
  id?: number
  plan_key?: string
  template_key?: string
  name?: string
  template_name?: string
  cycle_text?: string
  service_cycle_text?: string
  [key: string]: unknown
}

/** POST /api/admin/care-order/list 单条（与 Apipost 契约对齐，字段按需扩展） */
export interface CareOrderListItem {
  id: number
  order_id: string
  home_id: string
  user_id?: string
  subscription_id?: string
  report_id?: string
  plan_key?: string
  service_name?: string
  care_template_id?: number
  care_template?: CareOrderCareTemplate | null
  service_status?: string
  service_status_text?: string
  service_date?: string
  service_time_start?: string
  service_time_end?: string
  service_time_text?: string
  technician_id?: number
  technician?: Record<string, unknown> | null
  assignment_text?: string
  service_address?: string
  contact_name?: string
  contact_phone?: string
  subtotal_amount?: number
  tax_rate?: number
  tax_amount?: number
  total_amount?: number
  total_amount_text?: string
  payment_method?: string
  notes?: string
  placed_at?: string
  items?: CareOrderListItemChecklist[]
  followups?: CareOrderListFollowup[]
  has_follow_up?: number
  available_actions?: CareOrderAvailableActions | null
  /** 详情 Information Timeline（后端权威列表） */
  timeline?: CareOrderTimelineEvent[]
  /** 订单级首选时间列表（snake_case 与后端对齐；元素可为字符串或对象） */
  preferred_times?: unknown
  preferred_time_slots?: unknown
  /** 后端若直接返回 P/N/A/I/IC/U 数量，优先用于主行彩色码 */
  inspection_status_summary?: Record<string, unknown>
  /** 详情 Checklist 顶栏彩色码（P-x | B-x） */
  tags?: string | string[]
  detail_tags?: string | string[]
  checklist_summary?: unknown
  paid_at?: string
  create_time?: string
  update_time?: string
}

export interface CareOrderListItemChecklist {
  id?: number
  item_id?: string
  item_name?: string
  sort?: number
  inspection_category?: string
  category?: string
  item_status?: string
  status?: string
  /** 展示用 Routine / Baseline 文案 */
  type_text?: string
  /** 展示用检查状态；未检查时应为 Pending Care */
  display_status_text?: string
  tags?: string | string[]
  checklist_summary?: unknown
}

export interface CareOrderListFollowup {
  id?: number
  follow_up_id?: string
  source_order_item_id?: number | null
  status?: string
  status_text?: string
  follow_up_date?: string
  follow_up_time_start?: string
  follow_up_time_end?: string
  follow_up_time_text?: string
  technician_id?: number
  technician?: Record<string, unknown> | null
  assignment_text?: string
  service_address?: string
  notes?: string
}

export interface CareOrderListPagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

function technicianRefFromRow(t: Record<string, unknown> | null | undefined): string | undefined {
  if (!t) return undefined
  const no = t.staff_no ?? t.staffNo
  if (no != null && String(no).trim() !== '') return `@ ${String(no)}`
  return undefined
}

function technicianNameFromRow(t: Record<string, unknown> | null | undefined): string | undefined {
  if (!t) return undefined
  const n = t.name ?? t.label ?? t.display_name ?? t.displayName ?? t.username
  const s = n != null ? String(n).trim() : ''
  return s || undefined
}

function itemsProductCode(items: CareOrderListItemChecklist[] | undefined): string {
  if (!items?.length) return '—'
  const ids = items.map((x) => x.item_id).filter((x): x is string => Boolean(x && String(x).trim()))
  return ids.length ? ids.join(' · ') : '—'
}

/**
 * 主行彩色码含义（由 checklist 归类汇总）：
 * P — 待处理：基线 + 常规检查中所有待处理项总数
 * N — 正常/已完成：基线已完成数 + 常规检查正常数
 * A — 需留意：仅常规检查中标记需关注的数量
 * I — 重大故障：仅常规检查内判定为重大问题（无覆盖维修语义）
 * IC — 已报备维修的重大问题：仅当主单存在跟进订单时展示；常规检查纳入维修计划的重大问题数
 * B — 当前订单所有带 Baseline 标签的检查项总数
 * U — 无法完成/维修：基线 + 常规中未能完成检查或维修的项总数
 */
interface InspectionCounts {
  P: number
  N: number
  A: number
  I: number
  IC: number
  U: number
}

function emptyInspectionCounts(): InspectionCounts {
  return { P: 0, N: 0, A: 0, I: 0, IC: 0, U: 0 }
}

function totalInspectionCounts(c: InspectionCounts): number {
  return c.P + c.N + c.A + c.I + c.IC + c.U
}

/** 主单是否已有跟进订单（有则主行彩色码可展示 IC） */
export function careOrderRowHasFollowUpOrder(row: CareOrderListItem): boolean {
  if ((row.followups?.length ?? 0) > 0) return true
  if (row.has_follow_up === 1) return true
  const raw = row as unknown as Record<string, unknown>
  for (const k of ['has_follow_up', 'hasFollowUp', 'follow_up_count']) {
    const v = raw[k]
    if (v === 1 || v === true) return true
    if (typeof v === 'number' && v > 0) return true
  }
  return false
}

/** 无跟进订单时剔除 IC（计数并入 I） */
function applyInspectionIcFollowUpGate(c: InspectionCounts, hasFollowUp: boolean): InspectionCounts {
  if (hasFollowUp || c.IC <= 0) return c
  return { ...c, I: c.I + c.IC, IC: 0 }
}

/** 从已拼接的彩色码串中移除 IC-n */
export function stripIcTokensFromInspectionCode(code: string): string {
  const raw = (code ?? '').trim()
  if (!raw || raw === '—') return raw || '—'
  const parts = raw.split(/\s*·\s*/).map((s) => s.trim()).filter(Boolean)
  const rest = parts.filter((p) => !/^IC-\d+$/i.test(p))
  return rest.length ? rest.join(' · ') : '—'
}

function normBlob(s: string | undefined | null): string {
  return String(s ?? '')
    .trim()
    .toLowerCase()
}

function parseInspectionSummaryRaw(raw: unknown): InspectionCounts | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const num = (v: unknown): number => {
    if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.trunc(v))
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) return Math.max(0, Math.trunc(n))
    }
    return 0
  }
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      if (k in o) {
        const x = num(o[k])
        if (x > 0) return x
      }
    }
    return 0
  }
  const c = emptyInspectionCounts()
  c.P = pick('P', 'p', 'pending')
  c.N = pick('N', 'n', 'normal', 'completed_normal')
  c.A = pick('A', 'a', 'needs_attention')
  c.I = pick('I', 'i', 'major_issue')
  c.IC = pick('IC', 'ic', 'major_issue_covered')
  c.U = pick('U', 'u', 'unable')
  return totalInspectionCounts(c) > 0 ? c : null
}

function formatInspectionSegments(c: InspectionCounts): string {
  const order: (keyof InspectionCounts)[] = ['P', 'N', 'A', 'I', 'IC', 'U']
  const parts: string[] = []
  for (const k of order) {
    const n = c[k]
    if (n > 0) parts.push(k === 'IC' ? `IC-${n}` : `${k}-${n}`)
  }
  return parts.length ? parts.join(' · ') : '—'
}

/** 合并 checklist 上可用于阶段/状态判断的文本（含常见后续服务扩展字段） */
function checklistInspectionBlob(item: CareOrderListItemChecklist): string {
  const r = item as Record<string, unknown>
  const parts: string[] = [
    String(item.inspection_category ?? ''),
    String(item.category ?? ''),
    String(item.item_name ?? ''),
    String(item.item_status ?? ''),
    String(item.status ?? ''),
  ]
  for (const k of [
    'follow_up_status',
    'follow_up_service_status',
    'subsequent_service',
    'subsequent_service_status',
    'repair_status',
    'covered_repair_status',
    'fu_status',
  ]) {
    const v = r[k]
    if (typeof v === 'string' && v.trim()) parts.push(v)
  }
  for (const k of ['has_subsequent_service', 'has_follow_up_service', 'is_covered_repair']) {
    const v = r[k]
    if (v === 1 || v === true) parts.push('subsequent follow-up covered repair')
  }
  return normBlob(parts.join(' '))
}

function inferInspectionPhaseFromBlob(blob: string): 'baseline' | 'routine' | 'unknown' {
  if (blob.includes('routine')) return 'routine'
  if (blob.includes('baseline')) return 'baseline'
  return 'unknown'
}

function isUnableToCompleteOrRepair(st: string): boolean {
  if (!st) return false
  if (st.includes('unable to complete') || st.includes('unable to repair')) return true
  if (st.includes('unable to complete/repair')) return true
  if (st.includes('unable') && (st.includes('complet') || st.includes('repair'))) return true
  if (st.includes('无法完成') || st.includes('无法维修') || st.includes('无法修复')) return true
  return false
}

function isPendingInspectionState(st: string): boolean {
  if (!st) return false
  if (st.includes('pending')) return true
  if (st.includes('待检') || st.includes('待办') || st.includes('待处理') || st.includes('待排')) return true
  if (st.includes('待') && !st.includes('待定') && !st.includes('待命')) return true
  return false
}

function isBaselineCompleted(st: string): boolean {
  return (
    st.includes('completed') ||
    st.includes('complete') ||
    st.includes('inspection completed') ||
    st.includes('完成')
  )
}

function isRoutineNeedsAttention(st: string): boolean {
  return (
    st.includes('needs attention') ||
    st.includes('needs_attention') ||
    st.includes('need attention') ||
    st.includes('需要关注')
  )
}

function isRoutineMajorIssue(st: string): boolean {
  return (
    st.includes('major issue') ||
    st.includes('major_issue') ||
    st.includes('major-issue') ||
    st.includes('重大问题')
  )
}

/** Routine Major Issue 且含后续服务/覆盖维修语义 → IC，否则 → I */
function isMajorIssueCoveredRepairOrSubsequent(st: string): boolean {
  if (st.includes('covered repair') || st.includes('covered_repair')) return true
  if (st.includes('cover') && st.includes('repair')) return true
  if (st.includes('covered') && st.includes('repair')) return true
  if (st.includes('subsequent')) return true
  if (st.includes('follow-up') || st.includes('follow up') || st.includes('followup')) return true
  if (st.includes('follow_up')) return true
  if (st.includes('后续')) return true
  if (st.includes('覆盖维修') || st.includes('覆蓋維修')) return true
  if (st.includes('后续服务')) return true
  return false
}

/** Major Issue - Covered Repair 跟进结果：正常修复 vs 无法维修 */
export function inferCoveredRepairFollowUpOutcomeFromBlob(
  st: string,
): 'normal' | 'unable' | undefined {
  if (!st) return undefined
  if (
    st.includes('unable to repair') ||
    st.includes('unable_to_repair') ||
    st.includes('beyond repair') ||
    st.includes('beyond_repair') ||
    st.includes('无法维修') ||
    st.includes('无法修复')
  ) {
    return 'unable'
  }
  if (
    st.includes('follow_up_completed') ||
    st.includes('fu_completed') ||
    st.includes('followup_completed') ||
    st.includes('repair completed') ||
    st.includes('repaired') ||
    st.includes('restored to') ||
    st.includes('acceptable condition') ||
    (st.includes('completed') &&
      (st.includes('follow') || st.includes('repair') || st.includes('covered'))) ||
    (/\bnormal\b/.test(st) &&
      (st.includes('follow') || st.includes('repair') || st.includes('subsequent')))
  ) {
    return 'normal'
  }
  return undefined
}

/** 详情页：根据 checklist 行 + 订单跟进信息解析 Covered Repair 跟进结果 */
export function resolveCoveredRepairFollowUpOutcome(
  row: CareOrderChecklistDisplayRow,
  orderDetail?: CareOrderListItem | null,
  orderCard?: OrderCardDTO | null,
): 'normal' | 'unable' {
  if (row.coveredRepairOutcome) return row.coveredRepairOutcome

  if (row.orderItemId != null && orderDetail?.items?.length) {
    const item = orderDetail.items.find(
      (i) => resolveChecklistOrderItemId(i) === row.orderItemId,
    )
    if (item) {
      const fromItem = inferCoveredRepairFollowUpOutcomeFromBlob(checklistInspectionBlob(item))
      if (fromItem) return fromItem
    }
  }

  for (const fu of orderDetail?.followups ?? []) {
    if (
      row.orderItemId != null &&
      fu.source_order_item_id != null &&
      fu.source_order_item_id === row.orderItemId
    ) {
      const blob = normBlob(
        `${fu.status ?? ''} ${fu.status_text ?? ''} ${fu.notes ?? ''}`,
      )
      const fromFu = inferCoveredRepairFollowUpOutcomeFromBlob(blob)
      if (fromFu) return fromFu
    }
  }

  const orderBlob = normBlob(
    [
      orderDetail?.service_status,
      orderDetail?.service_status_text,
      orderCard?.status,
      orderCard?.serviceStatusCode,
    ]
      .filter((v) => typeof v === 'string' && v.trim())
      .join(' '),
  )
  const fromOrder = inferCoveredRepairFollowUpOutcomeFromBlob(orderBlob)
  if (fromOrder) return fromOrder

  return 'normal'
}

function isRoutineNormalOrCompleted(st: string): boolean {
  if (/\bnormal\b/.test(st) || st.includes('normal/')) return true
  if (st === 'pass' || st === 'ok' || st.includes(' passed')) return true
  if (st.includes('completed_normal')) return true
  if (st.includes('completed') || st.includes('complete')) return true
  if (st.includes('完成') && !st.includes('无法')) return true
  return false
}

function classifyChecklistToBucket(item: CareOrderListItemChecklist): keyof InspectionCounts | null {
  const st = checklistInspectionBlob(item)
  if (!st) return null

  const phase = inferInspectionPhaseFromBlob(st)
  const isRoutine = phase === 'routine'
  const isBaseline = phase === 'baseline'

  if (isBaseline || isRoutine) {
    if (isUnableToCompleteOrRepair(st)) return 'U'
    if (isPendingInspectionState(st)) return 'P'
  }

  if (isBaseline) {
    if (isBaselineCompleted(st)) return 'N'
    return null
  }

  if (isRoutine) {
    if (isRoutineMajorIssue(st)) {
      return isMajorIssueCoveredRepairOrSubsequent(st) ? 'IC' : 'I'
    }
    if (isRoutineNeedsAttention(st)) return 'A'
    if (isRoutineNormalOrCompleted(st)) return 'N'
    return null
  }

  if (isUnableToCompleteOrRepair(st)) return 'U'
  if (isPendingInspectionState(st)) return 'P'
  return null
}

function aggregateInspectionCountsFromItems(
  items: CareOrderListItemChecklist[] | undefined,
): (InspectionCounts & { B?: number }) | null {
  if (!items?.length) return null
  const c = emptyInspectionCounts()
  let baselineN = 0
  let any = false
  for (const it of items) {
    if (mapCareOrderChecklistDisplayRow(it).typeTag === 'Baseline') {
      baselineN++
      any = true
      continue
    }
    const b = classifyChecklistToBucket(it)
    if (b) {
      c[b] += 1
      any = true
    }
  }
  if (!any) return null
  return baselineN > 0 ? { ...c, B: baselineN } : c
}

function seededInspectionCounts(orderKey: string): InspectionCounts {
  let h = 2166136261
  for (let i = 0; i < orderKey.length; i++) h = Math.imul(h ^ orderKey.charCodeAt(i), 16777619)
  const u = () => (h = Math.imul(h ^ (h >>> 15), h | 1)) >>> 0
  const r = (mod: number) => u() % (mod + 1)
  const c = emptyInspectionCounts()
  c.P = r(4)
  c.N = 4 + r(18)
  c.A = r(4)
  c.I = r(7)
  c.IC = r(5)
  c.U = r(3)
  if (totalInspectionCounts(c) === 0) c.N = 1
  return c
}

function legacyInspectionTokenString(raw: string): string | null {
  const parts = raw
    .split(/\s*·\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (!parts.length) return null
  const re = /^(P|N|A|I|IC|U)-\d+$/i
  if (!parts.every((p) => re.test(p))) return null
  return parts
    .map((p) => {
      const m = p.match(/^(P|N|A|I|IC|U)-(\d+)$/i)
      if (!m || !m[1] || !m[2]) return p
      const k = m[1].toUpperCase()
      return k === 'IC' ? `IC-${m[2]}` : `${k}-${m[2]}`
    })
    .join(' · ')
}

/** 仅当订单 Service Status 为 Pending Schedule 语义（含待排期、不含 Reschedule）时，主行彩色码只显示 P；否则走完整 P/N/A/I/IC/U 汇总 */
function orderRowServiceStatusIsPendingScheduleForPOnly(row: CareOrderListItem): boolean {
  const a = normBlob(`${row.service_status_text ?? ''}`)
  const b = normBlob(`${row.service_status ?? ''}`)
  const isPendingScheduleBlob = (s: string) => {
    if (!s || !s.includes('pending')) return false
    if (s.includes('reschedule')) return false
    return s.includes('schedule') || s.includes('pending_schedule')
  }
  return isPendingScheduleBlob(a) || isPendingScheduleBlob(b)
}

function buildMainLineInspectionProductCode(row: CareOrderListItem): string {
  const tokens = careOrderCardInspectionTokensFromChecklistData(row)
  if (tokens.length) return tokens.join(' · ')

  if (orderRowServiceStatusIsPendingScheduleForPOnly(row)) {
    return 'P-1'
  }

  return '—'
}

function parsePreferredTimesArray(raw: unknown[]): string[] {
  const out: string[] = []
  for (const el of raw) {
    if (typeof el === 'string') {
      const s = el.trim()
      if (s) out.push(s)
    } else if (el && typeof el === 'object') {
      const o = el as Record<string, unknown>
      const cand = o.label ?? o.time_text ?? o.time ?? o.slot ?? o.text
      if (typeof cand === 'string' && cand.trim()) out.push(cand.trim())
    }
  }
  return out
}

export function preferredTimesFromCareOrderRow(row: CareOrderListItem): string[] {
  const r = row as unknown as Record<string, unknown>
  const keys = [
    'preferred_times',
    'preferredTimes',
    'preferred_time_slots',
    'service_preferred_times',
    'prefer_times',
    'preferredTimeList',
  ] as const
  for (const k of keys) {
    const raw = r[k]
    if (raw == null) continue
    if (typeof raw === 'string') {
      const s = raw.trim()
      if (!s) continue
      if (s.startsWith('[')) {
        try {
          const p = JSON.parse(s) as unknown
          if (Array.isArray(p)) {
            const a = parsePreferredTimesArray(p)
            if (a.length) return a
          }
        } catch {
          /* ignore */
        }
      }
      const parts = s
        .split(/[,;\n]+/)
        .map((x) => x.trim())
        .filter(Boolean)
      if (parts.length) return parts
      continue
    }
    if (Array.isArray(raw)) {
      const a = parsePreferredTimesArray(raw)
      if (a.length) return a
    }
  }
  return []
}

function isMainPendingScheduleServiceStatus(status: string | undefined): boolean {
  const raw = (status ?? '').trim()
  if (!raw) return false
  const bucket = classifyCareSidebarSubBucket(raw)
  if (bucket === 'pend_sched') return true
  const s = raw.toLowerCase().replace(/[\s-]+/g, '_')
  return s === 'pending_schedule' || (s.includes('pending') && s.includes('schedule') && !s.includes('reschedule') && !s.includes('follow') && !s.includes('fu_'))
}

function isMainPendingRescheduleServiceStatus(status: string | undefined): boolean {
  const raw = (status ?? '').trim()
  if (!raw) return false
  const bucket = classifyCareSidebarSubBucket(raw)
  if (bucket === 'pend_resched') return true
  const s = raw.toLowerCase().replace(/[\s-]+/g, '_')
  return (
    s === 'pending_reschedule' ||
    (s.includes('pending') && s.includes('reschedule') && !s.includes('follow') && !s.includes('fu_'))
  )
}

function isFuPendingRescheduleServiceStatus(status: string | undefined): boolean {
  const raw = (status ?? '').trim()
  if (!raw) return false
  const bucket = classifyCareSidebarSubBucket(raw)
  if (bucket === 'fu_pend_resched') return true
  const s = raw.toLowerCase().replace(/[\s-]+/g, '_')
  return (
    s.includes('fu_pending_reschedule') ||
    (s.includes('follow') && s.includes('pending') && s.includes('reschedule'))
  )
}

function isFuPendingScheduleServiceStatus(status: string | undefined): boolean {
  const raw = (status ?? '').trim()
  if (!raw) return false
  const bucket = classifyCareSidebarSubBucket(raw)
  if (bucket === 'fu_pend_sched') return true
  const s = raw.toLowerCase().replace(/[\s-]+/g, '_')
  return (
    s.includes('fu_pending_schedule') ||
    (s.includes('follow') && s.includes('pending') && s.includes('schedule') && !s.includes('reschedule'))
  )
}

function careStatusBlob(...parts: (string | undefined)[]): string {
  return parts
    .map((p) => p?.trim())
    .filter((p): p is string => Boolean(p))
    .join(' ')
    .toLowerCase()
}

function statusIndicatesCompleted(blob: string): boolean {
  if (!blob) return false
  if (blob.includes('incomplete')) return false
  if (blob.includes('pending')) return false
  if (/\bcompleted\b/.test(blob) || /\bcompeted\b/.test(blob)) return true
  if (/\bfu[\s_-]*completed\b/.test(blob) || /\bfollow[\s-]*up[\s_-]*completed\b/.test(blob)) {
    return true
  }
  return /\bcomplete\b/.test(blob)
}

/** 根据服务状态推断行操作能力（列表接口未返回布尔位时） */
function capsFromServiceStatus(status: string | undefined, statusText?: string) {
  const s = careStatusBlob(statusText, status)
  if (statusIndicatesCompleted(s)) {
    return {
      canSchedule: false,
      canReschedule: false,
      canRefund: false,
      canMessage: true,
      canNote: true,
    }
  }
  if (isFuPendingRescheduleServiceStatus(statusText) || isFuPendingRescheduleServiceStatus(status)) {
    return {
      canSchedule: false,
      canReschedule: true,
      canRefund: true,
      canMessage: true,
      canNote: true,
    }
  }
  if (isMainPendingRescheduleServiceStatus(statusText) || isMainPendingRescheduleServiceStatus(status)) {
    return {
      canSchedule: false,
      canReschedule: true,
      canRefund: true,
      canMessage: true,
      canNote: false,
    }
  }
  if (s.includes('pending_schedule')) {
    return {
      canSchedule: true,
      canReschedule: false,
      canRefund: true,
      canMessage: true,
      canNote: true,
    }
  }
  if (s.includes('assigned')) {
    return {
      canSchedule: false,
      canReschedule: true,
      canRefund: true,
      canMessage: true,
      canNote: true,
    }
  }
  return {
    canSchedule: true,
    canReschedule: true,
    canRefund: true,
    canMessage: true,
    canNote: true,
  }
}

function formatSlotForCard(raw: string): string {
  return formatOrderScheduleLineDisplay(raw.trim())
}

function isPendingScheduleStatusText(text: string | undefined): boolean {
  const s = (text ?? '').trim().toLowerCase()
  if (!s) return false
  if (s.includes('reschedule')) return false
  return s.includes('pending') && s.includes('schedule')
}

/** 从接口 `service_time_*` 解析已确认预约时段（与 service_status 无关） */
export function bookedTimeFromServiceFields(
  timeText: string | undefined,
  timeStart: string | undefined,
  timeEnd: string | undefined,
  serviceDateYmd?: string,
): string | null {
  const t = timeText?.trim()
  if (t) {
    const f = formatSlotForCard(t)
    if (f !== '—') return f
  }
  const a = timeStart?.trim()
  const b = timeEnd?.trim()
  if (a && b) {
    const line = formatServiceTimeRangeLine(a, b, serviceDateYmd?.trim())
    const f = formatSlotForCard(line)
    if (f !== '—') return f
  }
  if (a) {
    const f = formatSlotForCard(a)
    if (f !== '—') return f
  }
  return null
}

function readCareOrderRowString(row: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = row[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return undefined
}

/** 兼容 list/detail 中 snake_case、camelCase 及嵌套 schedule 字段 */
function normalizeCareOrderListRow(row: CareOrderListItem): CareOrderListItem {
  const raw = row as unknown as Record<string, unknown>
  const nestedRaw = raw.current_schedule ?? raw.currentSchedule ?? raw.schedule ?? raw.booking
  const nested =
    nestedRaw != null && typeof nestedRaw === 'object'
      ? (nestedRaw as Record<string, unknown>)
      : undefined

  return {
    ...row,
    service_date:
      row.service_date ??
      readCareOrderRowString(raw, 'serviceDate', 'scheduled_date', 'scheduledDate') ??
      (nested
        ? readCareOrderRowString(nested, 'date', 'service_date', 'serviceDate')
        : undefined),
    service_time_text:
      row.service_time_text ??
      readCareOrderRowString(
        raw,
        'serviceTimeText',
        'scheduled_time_text',
        'scheduledTimeText',
        'service_time',
      ) ??
      (nested
        ? readCareOrderRowString(nested, 'time_text', 'timeText', 'service_time_text')
        : undefined),
    service_time_start:
      row.service_time_start ??
      readCareOrderRowString(
        raw,
        'serviceTimeStart',
        'time_start',
        'timeStart',
        'scheduled_time_start',
      ) ??
      (nested ? readCareOrderRowString(nested, 'time_start', 'timeStart') : undefined),
    service_time_end:
      row.service_time_end ??
      readCareOrderRowString(
        raw,
        'serviceTimeEnd',
        'time_end',
        'timeEnd',
        'scheduled_time_end',
      ) ??
      (nested ? readCareOrderRowString(nested, 'time_end', 'timeEnd') : undefined),
    service_address:
      row.service_address ??
      readCareOrderRowString(raw, 'address', 'serviceAddress', 'short_address', 'shortAddress'),
    service_name:
      row.service_name ??
      readCareOrderRowString(raw, 'serviceName'),
    service_status:
      row.service_status ??
      readCareOrderRowString(raw, 'status', 'serviceStatus', 'service_status_text'),
    followups: row.followups?.map((fu) => normalizeFollowUpRow(fu)),
  }
}

export function bookedTimeFromCareOrderRow(row: CareOrderListItem): string | null {
  const normalized = normalizeCareOrderListRow(row)
  const booked = bookedTimeFromServiceFields(
    normalized.service_time_text,
    normalized.service_time_start,
    normalized.service_time_end,
    normalized.service_date,
  )
  if (booked) return booked

  const dateRaw = normalized.service_date?.trim()
  if (dateRaw && /(\d{1,2}:\d{2})/.test(dateRaw)) {
    const f = formatSlotForCard(dateRaw)
    if (f !== '—') return f
  }
  return null
}

function isFollowUpCareServiceLine(line: OrderServiceLineDTO): boolean {
  return (line.serviceName ?? '').trim().toLowerCase() === 'follow-up care'
}

function isPlaceholderFollowUpCareLine(line: OrderServiceLineDTO): boolean {
  if (!isFollowUpCareServiceLine(line)) return false
  const assign = (line.assignment ?? '').trim()
  const pendingAssign = !assign || /^pending\s+assignment$/i.test(assign)
  const noTech = !line.technicianRef?.trim()
  const noFuId = line.followUpPrimaryId == null || line.followUpPrimaryId <= 0
  const noProduct = !(line.productCode ?? '').trim() || line.productCode === '—'
  return pendingAssign && noTech && noFuId && noProduct
}

/** 列表同时带 followups 与占位跟进行时，去掉 Pending Assignment 占位行 */
export function dedupePlaceholderFollowUpCareLines(services: OrderServiceLineDTO[]): OrderServiceLineDTO[] {
  const fuIndexes = services
    .map((l, i) => (isFollowUpCareServiceLine(l) ? i : -1))
    .filter((i) => i >= 0)
  if (fuIndexes.length < 2) return services
  const hasRealFu = fuIndexes.some((i) => {
    const l = services[i]!
    return (
      (l.followUpPrimaryId != null && l.followUpPrimaryId > 0) ||
      ((l.productCode ?? '').trim() !== '' && l.productCode !== '—')
    )
  })
  if (!hasRealFu) return services
  return services.filter((l) => !isPlaceholderFollowUpCareLine(l))
}

/** 兼容 follow-up 行 snake_case、camelCase 及嵌套 schedule 字段 */
function normalizeFollowUpRow(fu: CareOrderListFollowup): CareOrderListFollowup {
  const raw = fu as unknown as Record<string, unknown>
  const nestedRaw = raw.current_schedule ?? raw.currentSchedule ?? raw.schedule ?? raw.booking
  const nested =
    nestedRaw != null && typeof nestedRaw === 'object'
      ? (nestedRaw as Record<string, unknown>)
      : undefined
  const id =
    readCareOrderNumericId(fu.id) ??
    readCareOrderNumericId(raw.target_id ?? raw.targetId) ??
    readCareOrderNumericId(raw.followup_id ?? raw.followUpId)

  return {
    ...fu,
    id,
    follow_up_date:
      fu.follow_up_date ??
      readCareOrderRowString(raw, 'followUpDate', 'follow_up_date', 'date', 'service_date') ??
      (nested ? readCareOrderRowString(nested, 'date', 'follow_up_date', 'service_date') : undefined),
    follow_up_time_text:
      fu.follow_up_time_text ??
      readCareOrderRowString(
        raw,
        'followUpTimeText',
        'follow_up_time_text',
        'time_text',
        'scheduled_time_text',
      ) ??
      (nested ? readCareOrderRowString(nested, 'time_text', 'timeText', 'follow_up_time_text') : undefined),
    follow_up_time_start:
      fu.follow_up_time_start ??
      readCareOrderRowString(raw, 'followUpTimeStart', 'follow_up_time_start', 'time_start', 'timeStart') ??
      (nested ? readCareOrderRowString(nested, 'time_start', 'timeStart') : undefined),
    follow_up_time_end:
      fu.follow_up_time_end ??
      readCareOrderRowString(raw, 'followUpTimeEnd', 'follow_up_time_end', 'time_end', 'timeEnd') ??
      (nested ? readCareOrderRowString(nested, 'time_end', 'timeEnd') : undefined),
  }
}

export function bookedTimeFromFollowUpRow(fu: CareOrderListFollowup): string | null {
  const normalized = normalizeFollowUpRow(fu)
  return bookedTimeFromServiceFields(
    normalized.follow_up_time_text,
    normalized.follow_up_time_start,
    normalized.follow_up_time_end,
    normalized.follow_up_date,
  )
}

/** 列表/卡片：是否为已确认预约时段（非 Pending Schedule / Reschedule 占位） */
export function isBookedScheduleDisplayLabel(label: string | null | undefined): boolean {
  const s = (label ?? '').trim()
  if (!s || s === '—') return false
  if (isPendingScheduleStatusText(s)) return false
  if (/^pending\s+reschedule$/i.test(s)) return false
  if (/^follow-up-pending\s+schedule$/i.test(s)) return false
  return /(\d{1,2}:\d{2})\s*(AM|PM)/i.test(s)
}

function mainScheduledDisplay(row: CareOrderListItem): string | null {
  const normalized = normalizeCareOrderListRow(row)
  const booked = bookedTimeFromCareOrderRow(normalized)
  const isPendingResched =
    isMainPendingRescheduleServiceStatus(normalized.service_status) ||
    isMainPendingRescheduleServiceStatus(normalized.service_status_text)

  if (isPendingResched) {
    if (booked) return booked
    const st = normalized.service_status_text?.trim()
    if (st) return st
    return 'Pending Reschedule'
  }

  if (booked) return booked

  const hasFollowUp = (normalized.followups?.length ?? 0) > 0
  if (hasFollowUp) {
    const dateRaw = normalized.service_date?.trim()
    if (dateRaw) {
      const f = formatSlotForCard(dateRaw)
      return f === '—' ? dateRaw : f
    }
    return null
  }
  const st = normalized.service_status_text?.trim()
  if (st && !isPendingScheduleStatusText(st)) return st
  return null
}

function followUpScheduledDisplay(fu: CareOrderListFollowup): string | null {
  const normalized = normalizeFollowUpRow(fu)
  const booked = bookedTimeFromServiceFields(
    normalized.follow_up_time_text,
    normalized.follow_up_time_start,
    normalized.follow_up_time_end,
    normalized.follow_up_date,
  )
  const isPendingSched =
    isFuPendingScheduleServiceStatus(normalized.status) ||
    isFuPendingScheduleServiceStatus(normalized.status_text)
  if (isPendingSched) {
    if (booked) return booked
    const st = normalized.status_text?.trim()
    if (st) return st
    return 'Follow-up-Pending Schedule'
  }

  const isPendingResched =
    isFuPendingRescheduleServiceStatus(normalized.status) ||
    isFuPendingRescheduleServiceStatus(normalized.status_text)

  if (isPendingResched) {
    if (booked) return booked
    const st = normalized.status_text?.trim()
    if (st) return st
    return 'Pending Reschedule'
  }

  if (booked) return booked

  const dateRaw = normalized.follow_up_date?.trim()
  if (dateRaw) {
    const f = formatSlotForCard(dateRaw)
    if (f !== '—') return f
  }

  const st = normalized.status_text?.trim()
  if (st) return st
  return null
}

function formatServiceStatusFallback(code: string | undefined): string | undefined {
  const raw = code?.trim()
  if (!raw) return undefined
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function resolveCareOrderDisplayId(row: CareOrderListItem): string {
  const fromOrderId = String(row.order_id ?? '').trim()
  if (fromOrderId) return fromOrderId
  if (typeof row.id === 'number' && Number.isFinite(row.id)) return `HM-${row.id}`
  return '—'
}

/** 解析订单/跟进行主键（兼容 number 与字符串数字） */
export function readCareOrderNumericId(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.trunc(raw)
  const asStr = String(raw ?? '').trim()
  if (/^\d+$/.test(asStr)) {
    const n = Number(asStr)
    if (Number.isFinite(n) && n > 0) return Math.trunc(n)
  }
  return undefined
}

/** 业务展示号 order_id（如 20260713110501）不是 care-schedule assign 的 DB 主键 */
export function isLikelyCareOrderBusinessNumber(n: number): boolean {
  return Number.isFinite(n) && Math.trunc(n) >= 100_000_000
}

/** 列表/详情 row.id → 数据库主键；排除误把 order_id 当 id 的长数字 */
export function resolveCareOrderDatabasePrimaryId(raw: unknown): number | undefined {
  const pk = readCareOrderNumericId(raw)
  if (pk == null || pk <= 0 || isLikelyCareOrderBusinessNumber(pk)) return undefined
  return pk
}

function mergeOrderPrimaryIdField(existing: OrderCardDTO, incoming: OrderCardDTO): number | undefined {
  const pick = (pk?: number) => {
    if (pk == null || pk <= 0 || isLikelyCareOrderBusinessNumber(pk)) return undefined
    return Math.trunc(pk)
  }
  return pick(incoming.orderPrimaryId) ?? pick(existing.orderPrimaryId)
}

export function resolveFollowUpRowPrimaryId(fu: CareOrderListFollowup): number | undefined {
  const raw = fu as unknown as Record<string, unknown>
  return (
    readCareOrderNumericId(fu.id) ??
    readCareOrderNumericId(raw.target_id ?? raw.targetId) ??
    readCareOrderNumericId(raw.followup_id ?? raw.followUpId)
  )
}

export function resolveFollowUpPrimaryIdFromOrderCard(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
): number | undefined {
  if (line.followUpPrimaryId != null && line.followUpPrimaryId > 0) {
    return Math.trunc(line.followUpPrimaryId)
  }
  for (const svc of order.services) {
    const name = (svc.serviceName ?? '').trim().toLowerCase()
    if (name !== 'follow-up care') continue
    const pk = svc.followUpPrimaryId
    if (pk != null && pk > 0) return Math.trunc(pk)
  }
  return undefined
}

/** 列表行主键（与详情 POST `id` 一致）；仅用 row.id，不用展示 order_id */
function resolveCareOrderRowPrimaryId(row: CareOrderListItem): number | undefined {
  return resolveCareOrderDatabasePrimaryId(row.id)
}

/**
 * 排程 context / assign 主单 `target_id`（`target_type: order`）。
 * 必须为数据库主键 `orderPrimaryId`，不是卡片展示号 `order.id`。
 */
export function resolveScheduleOrderTargetId(order: OrderCardDTO): number | undefined {
  const pk = order.orderPrimaryId
  if (pk != null && pk > 0 && !isLikelyCareOrderBusinessNumber(pk)) return Math.trunc(pk)
  const hm = (order.id ?? '').trim().match(/^HM-(\d+)$/i)
  if (hm?.[1]) return resolveCareOrderDatabasePrimaryId(hm[1])
  return undefined
}

/** 拉详情 / assign 前解析主单 DB 主键（不用展示 order_id） */
export function resolveMainOrderPrimaryIdFromCard(order: OrderCardDTO): number | undefined {
  return resolveScheduleOrderTargetId(order)
}

/** 从 list 响应 data 中提取订单数组（兼容 list / items / orders / records / rows） */
export function extractCareOrderListArray(data: Record<string, unknown>): unknown[] | null {
  for (const key of ['list', 'items', 'orders', 'records', 'rows'] as const) {
    const v = data[key]
    if (Array.isArray(v)) return v
  }
  return null
}

function mainRowServiceStatusIsAssignedOrBeyond(code: string | undefined, text?: string): boolean {
  const blob = `${code ?? ''} ${text ?? ''}`.trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (!blob) return false
  if (blob.includes('pending')) return false
  return blob.includes('assigned') || blob.includes('completed') || blob.includes('complete')
}

function followUpListItemSidebarBucket(
  fu: CareOrderListFollowup,
  mainStatus?: string,
  mainStatusText?: string,
): CareSidebarSubBucket | null {
  for (const cand of [fu.status, fu.status_text]) {
    const bucket = classifyCareSidebarSubBucket(cand ?? '')
    if (bucket === 'fu_pend_sched' || bucket === 'fu_pend_resched') return bucket
  }
  if (!mainRowServiceStatusIsAssignedOrBeyond(mainStatus, mainStatusText)) return null
  const fuCode = (fu.status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  const fuText = (fu.status_text ?? '').trim().toLowerCase()
  if (
    fuCode === 'pending_schedule' ||
    fuCode === 'fu_pending_schedule' ||
    fuCode === 'follow_up_pending_schedule' ||
    (fuText.includes('pending') && fuText.includes('schedule') && !fuText.includes('reschedule'))
  ) {
    return 'fu_pend_sched'
  }
  if (
    fuCode === 'pending_reschedule' ||
    fuCode === 'fu_pending_reschedule' ||
    fuCode === 'follow_up_pending_reschedule' ||
    (fuText.includes('pending') && fuText.includes('reschedule'))
  ) {
    return 'fu_pend_resched'
  }
  return null
}

function followUpRowStatusFromListItem(row: CareOrderListItem): {
  status: string
  serviceStatusCode?: string
} | null {
  const raw = row as unknown as Record<string, unknown>
  const rowLevelKeys = [
    'follow_up_service_status',
    'follow_up_status',
    'fu_service_status',
  ] as const
  for (const key of rowLevelKeys) {
    const code = raw[key]
    if (typeof code !== 'string' || !code.trim()) continue
    const label = careServiceStatusCodeToDisplayLabel(code)
    if (label === CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE) {
      return { status: label, serviceStatusCode: code.trim() }
    }
    if (label === CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE) {
      return { status: label, serviceStatusCode: code.trim() }
    }
    if (label === CARE_SERVICE_STATUS_FU_ASSIGNED) {
      return { status: label, serviceStatusCode: code.trim() }
    }
    if (label === CARE_SERVICE_STATUS_FU_COMPLETED) {
      return { status: label, serviceStatusCode: code.trim() }
    }
    const bucket = classifyCareSidebarSubBucket(code)
    if (bucket === 'fu_pend_sched') {
      return { status: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE, serviceStatusCode: code.trim() }
    }
    if (bucket === 'fu_pend_resched') {
      return { status: CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE, serviceStatusCode: code.trim() }
    }
  }

  for (const fu of row.followups ?? []) {
    const fuCode = (fu.status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')
    const fuText = (fu.status_text ?? '').trim().toLowerCase()
    const fromCode = careServiceStatusCodeToDisplayLabel(fu.status)
    if (fromCode === CARE_SERVICE_STATUS_FU_COMPLETED || fuCode.includes('completed')) {
      return { status: CARE_SERVICE_STATUS_FU_COMPLETED, serviceStatusCode: fu.status?.trim() || 'fu_completed' }
    }
    if (fromCode === CARE_SERVICE_STATUS_FU_ASSIGNED) {
      return { status: CARE_SERVICE_STATUS_FU_ASSIGNED, serviceStatusCode: fu.status?.trim() || 'fu_assigned' }
    }
    if (
      fuCode === 'fu_assigned' ||
      fuCode === 'follow_up_assigned' ||
      (fuCode === 'assigned' && !fuText.includes('unassigned'))
    ) {
      return { status: CARE_SERVICE_STATUS_FU_ASSIGNED, serviceStatusCode: fu.status?.trim() || 'fu_assigned' }
    }

    const bucket = followUpListItemSidebarBucket(fu, row.service_status, row.service_status_text)
    if (bucket === 'fu_pend_sched') {
      return {
        status: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
        serviceStatusCode: fu.status?.trim() || 'fu_pending_schedule',
      }
    }
    if (bucket === 'fu_pend_resched') {
      return {
        status: CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE,
        serviceStatusCode: fu.status?.trim() || 'fu_pending_reschedule',
      }
    }

    if (
      mainServiceStatusIsAssignedOrBeyond(row.service_status, row.service_status_text) &&
      followUpScheduledDisplay(fu) &&
      (fu.technician || fu.assignment_text?.trim() || fuText.includes('assigned'))
    ) {
      return { status: CARE_SERVICE_STATUS_FU_ASSIGNED, serviceStatusCode: 'fu_assigned' }
    }

    if (
      mainServiceStatusIsAssignedOrBeyond(row.service_status, row.service_status_text) &&
      (fuText.includes('pending') && fuText.includes('reschedule'))
    ) {
      return {
        status: CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE,
        serviceStatusCode: fu.status?.trim() || 'fu_pending_reschedule',
      }
    }
  }
  return null
}

function resolveFollowUpAwareListRowStatus(row: CareOrderListItem): {
  status: string
  serviceStatusCode?: string
} | null {
  if (!careOrderRowHasFollowUpOrder(row)) return null

  const fuStatus = followUpRowStatusFromListItem(row)
  if (fuStatus) return fuStatus

  if (mainServiceStatusIsAssignedOrBeyond(row.service_status, row.service_status_text)) {
    return {
      status: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
      serviceStatusCode: 'fu_pending_schedule',
    }
  }

  return null
}

function resolveCareOrderListRowCardStatus(row: CareOrderListItem): {
  status: string
  serviceStatusCode?: string
} {
  const fuAware = resolveFollowUpAwareListRowStatus(row)
  if (fuAware) return fuAware

  const mainStatus =
    row.service_status_text?.trim() ||
    formatServiceStatusFallback(row.service_status) ||
    row.service_status?.trim() ||
    '—'
  const fromCode = careServiceStatusCodeToDisplayLabel(row.service_status)
  return {
    status: fromCode ?? mainStatus,
    serviceStatusCode: row.service_status?.trim() || undefined,
  }
}

function isPendingAssignmentLabel(text: string | undefined): boolean {
  const s = (text ?? '').trim()
  if (!s) return true
  return /^pending\s+assignment$/i.test(s)
}

/** 列表 DTO：未指派且无确定时段的跟进行 FU Pending Reschedule → FU Pending Schedule */
function repairMisclassifiedFuPendingRescheduleCard(card: OrderCardDTO): OrderCardDTO {
  if (classifyCareSidebarSubBucket(card.status ?? '') !== 'fu_pend_resched') return card
  const fuIdx = card.services.findIndex(
    (line) => line.serviceName.trim().toLowerCase() === 'follow-up care',
  )
  if (fuIdx < 0) return card
  const fu = card.services[fuIdx]!
  if (fu.technicianRef?.trim()) return card
  if (!isPendingAssignmentLabel(fu.assignment)) return card
  const sched = (fu.scheduledAt ?? '').trim()
  if (
    sched &&
    sched !== '—' &&
    !/^pending\s+(schedule|reschedule)/i.test(sched) &&
    !/^follow-up-pending\s+(schedule|reschedule)/i.test(sched) &&
    /(\d{1,2}:\d{2})\s*(AM|PM)/i.test(sched)
  ) {
    return card
  }

  const services = card.services.map((line, i) => {
    if (i !== fuIdx) return line
    return {
      ...line,
      scheduledAt: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
      assignment: 'Pending Assignment',
      technicianRef: undefined,
      canSchedule: true,
      canReschedule: false,
    }
  })

  return {
    ...card,
    status: CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE,
    serviceStatusCode: 'fu_pending_schedule',
    services,
  }
}

/** 列表 DTO：未指派且无确定时段的主单 Pending Reschedule → Pending Schedule（接口已标 pending_reschedule 时以接口为准） */
function repairMisclassifiedMainPendingRescheduleCard(card: OrderCardDTO): OrderCardDTO {
  const apiCode = (card.serviceStatusCode ?? '').trim().toLowerCase()
  if (apiCode === 'pending_reschedule') return card
  if (classifyCareSidebarSubBucket(card.status ?? '') !== 'pend_resched') return card
  const fuLines = card.services.filter(
    (line) => line.serviceName.trim().toLowerCase() === 'follow-up care',
  )
  const mainLines = card.services.filter(
    (line) => line.serviceName.trim().toLowerCase() !== 'follow-up care',
  )
  if (fuLines.length > 0 && mainLines.length === 0) {
    return repairMisclassifiedFuPendingRescheduleCard({
      ...card,
      status: CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE,
      serviceStatusCode: 'fu_pending_reschedule',
    })
  }
  if (fuLines.length > 0) return card
  const mainLine = card.services[0]
  if (!mainLine) return card
  if (mainLine.technicianRef?.trim()) return card
  if (!isPendingAssignmentLabel(mainLine.assignment)) return card
  const sched = (mainLine.scheduledAt ?? '').trim()
  if (
    sched &&
    sched !== '—' &&
    !/^pending\s+(schedule|reschedule)/i.test(sched) &&
    /(\d{1,2}:\d{2})\s*(AM|PM)/i.test(sched)
  ) {
    return card
  }

  const services = card.services.map((line, i) => {
    if (i !== 0) return line
    const nextScheduledAt =
      !sched || sched === '—' || /\bpending\s+reschedule\b/i.test(sched)
        ? CARE_SERVICE_STATUS_PENDING_SCHEDULE
        : line.scheduledAt
    return {
      ...line,
      scheduledAt: nextScheduledAt,
      canSchedule: true,
      canReschedule: false,
      canNote: true,
    }
  })

  return {
    ...card,
    status: CARE_SERVICE_STATUS_PENDING_SCHEDULE,
    serviceStatusCode: 'pending_schedule',
    services,
  }
}

export function careOrderListItemToCardDto(row: CareOrderListItem): OrderCardDTO {
  const normalizedRow = normalizeCareOrderListRow(row)
  const cardStatus = resolveCareOrderListRowCardStatus(normalizedRow)
  const cardLevelMainPending =
    (cardStatus.serviceStatusCode ?? '').trim().toLowerCase() === 'pending_schedule' ||
    cardStatus.status === CARE_SERVICE_STATUS_PENDING_SCHEDULE
  const cardLevelFuPending =
    (cardStatus.serviceStatusCode ?? '').trim().toLowerCase() === 'fu_pending_schedule' ||
    cardStatus.status === CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE

  const mainScheduleLabel = mainScheduledDisplay(normalizedRow)
  const mainCaps = capsFromServiceStatus(normalizedRow.service_status, normalizedRow.service_status_text)
  const isMainPendingSched =
    isMainPendingScheduleServiceStatus(normalizedRow.service_status) ||
    isMainPendingScheduleServiceStatus(normalizedRow.service_status_text) ||
    cardLevelMainPending
  const mainTech = normalizedRow.technician
  const mainTechObj = mainTech && typeof mainTech === 'object' ? (mainTech as Record<string, unknown>) : null
  const mainName = technicianNameFromRow(mainTechObj)
  const mainRef = technicianRefFromRow(mainTechObj)
  const mainBookedWhilePending =
    isMainPendingSched && isBookedScheduleDisplayLabel(mainScheduleLabel)
  const mainScheduledAt = isMainPendingSched
    ? mainBookedWhilePending
      ? mainScheduleLabel!
      : CARE_SERVICE_STATUS_PENDING_SCHEDULE
    : mainScheduleLabel
  const mainAssignment = isMainPendingSched
    ? mainBookedWhilePending
      ? mainName && String(mainName).trim() !== ''
        ? String(mainName)
        : normalizedRow.assignment_text || 'Pending Assignment'
      : 'Pending Assignment'
    : mainName && String(mainName).trim() !== ''
      ? String(mainName)
      : normalizedRow.assignment_text || 'Pending Assignment'

  const priceText = normalizedRow.total_amount_text?.trim() || '—'
  const payMethod = normalizedRow.payment_method?.trim() || '—'

  /** context follow-up 精简行：主行误标 Follow-up Care 时只保留 followups[] 生成的服务行 */
  const fuOnlyContextRow =
    (normalizedRow.followups?.length ?? 0) > 0 &&
    (normalizedRow.service_name?.trim().toLowerCase() === 'follow-up care')

  const mainLine: OrderServiceLineDTO = {
    id: normalizedRow.order_id ? `main-${normalizedRow.order_id}` : undefined,
    serviceName: normalizedRow.service_name?.trim() || 'Care',
    productCode: buildMainLineInspectionProductCode(normalizedRow),
    scheduledAt: mainScheduledAt,
    address: normalizedRow.service_address?.trim() || '—',
    assignment: mainAssignment,
    technicianRef: isMainPendingSched && !mainBookedWhilePending ? undefined : mainRef,
    price: priceText,
    paymentMethod: payMethod,
    canSchedule: isMainPendingSched && !mainBookedWhilePending ? true : mainBookedWhilePending ? false : mainCaps.canSchedule,
    canReschedule: isMainPendingSched && !mainBookedWhilePending ? false : mainBookedWhilePending ? true : mainCaps.canReschedule,
    canRefund: mainCaps.canRefund,
    canMessage: mainCaps.canMessage,
    canNote: mainCaps.canNote,
  }

  if ((normalizedRow.followups?.length ?? 0) > 0 && !fuOnlyContextRow) {
    mainLine.canSchedule = false
    const sched = (mainLine.scheduledAt ?? '').trim()
    if (!sched || sched === '—' || isPendingScheduleStatusText(sched) || /^pending\s+schedule$/i.test(sched)) {
      const rebuilt = mainScheduledDisplay(normalizedRow)
      if (rebuilt) mainLine.scheduledAt = rebuilt
      else if (normalizedRow.service_date?.trim()) mainLine.scheduledAt = normalizedRow.service_date.trim()
    }
  }

  const services: OrderServiceLineDTO[] = fuOnlyContextRow ? [] : [mainLine]

  if (
    !fuOnlyContextRow &&
    careOrderRowHasFollowUpOrder(normalizedRow) &&
    (normalizedRow.followups?.length ?? 0) === 0 &&
    !services.some((l) => (l.serviceName ?? '').trim().toLowerCase() === 'follow-up care')
  ) {
    const fuPending =
      cardLevelFuPending ||
      mainServiceStatusIsAssignedOrBeyond(
        normalizedRow.service_status,
        normalizedRow.service_status_text,
      )
    services.push({
      serviceName: 'Follow-up Care',
      productCode: '—',
      scheduledAt: fuPending ? CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE : '—',
      address: mainLine.address,
      assignment: 'Pending Assignment',
      price: '—',
      paymentMethod: payMethod,
      canSchedule: fuPending,
      canReschedule: false,
      canRefund: false,
      canMessage: true,
      canNote: true,
    })
  }

  for (const fu of normalizedRow.followups ?? []) {
    const caps = capsFromServiceStatus(fu.status, fu.status_text)
    const isPendingSched =
      isFuPendingScheduleServiceStatus(fu.status) ||
      isFuPendingScheduleServiceStatus(fu.status_text) ||
      cardLevelFuPending
    const fuTech = fu.technician
    const fuObj = fuTech && typeof fuTech === 'object' ? (fuTech as Record<string, unknown>) : null
    const fuName = technicianNameFromRow(fuObj)
    const fuRef = technicianRefFromRow(fuObj)
    const fuDisplay = followUpScheduledDisplay(fu)
    const fuBookedWhilePending = isPendingSched && isBookedScheduleDisplayLabel(fuDisplay)
    const fuAssignment = isPendingSched
      ? fuBookedWhilePending
        ? fuName && String(fuName).trim() !== ''
          ? String(fuName)
          : fu.assignment_text || 'Pending Assignment'
        : 'Pending Assignment'
      : fuName && String(fuName).trim() !== ''
        ? String(fuName)
        : fu.assignment_text || 'Pending Assignment'
    const fuTime = isPendingSched
      ? fuBookedWhilePending
        ? fuDisplay!
        : CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE
      : fuDisplay
    const fuPrimaryId = resolveFollowUpRowPrimaryId(fu)
    services.push({
      id: fu.follow_up_id ? `fu-${fu.follow_up_id}` : undefined,
      followUpPrimaryId: fuPrimaryId,
      serviceName: 'Follow-up Care',
      productCode: fu.follow_up_id?.trim() || '—',
      scheduledAt: fuTime,
      address: (fu.service_address ?? row.service_address)?.trim() || '—',
      assignment: fuAssignment,
      technicianRef: isPendingSched && !fuBookedWhilePending ? undefined : fuRef,
      price: '—',
      paymentMethod: payMethod,
      canSchedule: isPendingSched && !fuBookedWhilePending ? true : caps.canSchedule,
      canReschedule: isPendingSched && !fuBookedWhilePending ? false : caps.canReschedule,
      canRefund: caps.canRefund,
      canMessage: caps.canMessage,
      canNote: caps.canNote,
    })
  }

  const placed = normalizedRow.placed_at?.trim() || normalizedRow.create_time?.trim() || ''
  const preferredTimes = preferredTimesFromCareOrderRow(normalizedRow)

  const placementDate = placed ? formatOrderScheduleLineDisplay(placed) : ''

  const serviceDate = normalizedRow.service_date?.trim() || undefined

  const resolvedNotes = resolveNotesFromCareOrderListRow(normalizedRow)
  const dedupedServices = dedupePlaceholderFollowUpCareLines(services)

  return repairMisclassifiedFuPendingRescheduleCard(
    repairMisclassifiedMainPendingRescheduleCard({
      id: resolveCareOrderDisplayId(normalizedRow),
      orderPrimaryId: resolveCareOrderRowPrimaryId(normalizedRow),
      homeId: normalizedRow.home_id,
      status: cardStatus.status,
      serviceStatusCode: cardStatus.serviceStatusCode,
      serviceDate,
      placementDate,
      notes: resolvedNotes,
      preferredTimes: preferredTimes.length ? preferredTimes : undefined,
      services: dedupedServices,
    }),
  )
}

function readNumericField(candidates: unknown[]): number {
  for (const v of candidates) {
    if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.trunc(v))
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) return Math.max(0, Math.trunc(n))
    }
  }
  return 0
}

/** 从分页或 data 上读取列表总条数（兼容 number / string 及常见字段名） */
export function readCareOrderListTotal(data: Record<string, unknown>, listLength: number): number {
  const pag = data.pagination
  const p = pag && typeof pag === 'object' ? (pag as Record<string, unknown>) : undefined
  const total = readNumericField([
    p?.total,
    p?.total_count,
    p?.record_total,
    data.total,
    data.total_count,
  ])
  if (total > 0) return total
  return listLength
}

/** 列表接口分页总页数（`pagination.total_pages` 等） */
export function readCareOrderListPageCount(data: Record<string, unknown>): number {
  const pag = data.pagination
  const p = pag && typeof pag === 'object' ? (pag as Record<string, unknown>) : undefined
  return readNumericField([p?.total_pages, p?.totalPages, data.total_pages, data.totalPages])
}

/** 解析 POST care-order/list 的完整响应为卡片列表与 total */
export function parseCareOrderListResponse(raw: unknown): { list: OrderCardDTO[]; total: number } | null {
  if (raw == null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (r.code !== 200 || r.data == null || typeof r.data !== 'object') return null
  const data = r.data as Record<string, unknown>
  const listRaw = extractCareOrderListArray(data)
  if (!listRaw) return null
  const list = listRaw.map((x) => careOrderListItemToCardDto(x as CareOrderListItem))
  const total = readCareOrderListTotal(data, list.length)
  return { list, total }
}

/** 将列表 DTO 转为侧栏统计用的 OrderRecord（仅保证 count 所需字段） */
export function orderCardDtoToSidebarRecord(o: OrderCardDTO): OrderRecord {
  return {
    orderId: o.id,
    homeId: o.homeId,
    serviceStatusLabel: o.status,
    placedOnLabel: o.placementDate,
    lines: [],
    technician: null,
    payment: { amount: '', method: '' },
    useScheduleLabel: true,
    scheduleDisabled: false,
    rescheduleDisabled: false,
    refundDisabled: false,
    messageDisabled: false,
    notesDisabled: false,
  }
}

/** POST `/api/admin/care-order/create` 请求体（raw-json） */
export interface CareOrderCreateRequest {
  order_id: string
  home_id: string
  plan_key: string
  care_template_id: number
  /** 服务周期日 YYYY-MM-DD（新建订单表单 service sycle） */
  service_date?: string
  service_address: string
  contact_name: string
  contact_phone: string
  notes?: string
}

/** POST `/api/admin/care-order/create`，成功返回新建订单详情（与列表单项同形） */
export async function postCareOrderCreate(body: CareOrderCreateRequest): Promise<CareOrderListItem> {
  const raw = await request.post('/api/admin/care-order/create', body)
  const r = raw as { code?: number; msg?: string; data?: CareOrderListItem }
  if (r?.code === 200 && r.data && typeof r.data === 'object') return r.data
  const msg = typeof r?.msg === 'string' ? r.msg : 'Failed to create order'
  throw new Error(msg)
}

function isCareOrderListItemShape(row: unknown): row is CareOrderListItem {
  if (!row || typeof row !== 'object') return false
  const o = row as Record<string, unknown>
  return typeof o.order_id === 'string' || typeof o.id === 'number'
}

/** care-order 写接口：204 / 空 body / `{ code: 200 }` 均视为成功 */
function careOrderMutationSucceeded(raw: unknown): boolean {
  if (raw == null || raw === '') return true
  if (typeof raw !== 'object') return false
  const r = raw as Record<string, unknown>
  if (Object.keys(r).length === 0) return true
  const code = r.code
  return code === 200 || code === '200' || code === 204 || code === '204'
}

/** 解析详情接口原始响应为单条订单数据 */
export function parseCareOrderDetailResponse(raw: unknown): CareOrderListItem | null {
  if (raw == null || raw === '') return null
  if (typeof raw !== 'object') return null

  if (isCareOrderListItemShape(raw)) return raw

  const r = raw as Record<string, unknown>
  const code = r.code
  if (code === 200 || code === '200' || code === 204 || code === '204') {
    const data = r.data
    if (isCareOrderListItemShape(data)) return data
    if (data && typeof data === 'object') {
      const d = data as Record<string, unknown>
      for (const key of ['order', 'order_detail', 'detail', 'row', 'target'] as const) {
        const nested = d[key]
        if (isCareOrderListItemShape(nested)) return nested
      }
    }
  }
  return null
}

/** POST `/api/admin/care-order/detail`，body `{ id }` 为订单主键（int） */
export async function postCareOrderDetail(orderPrimaryId: number): Promise<CareOrderListItem> {
  const raw = await request.post('/api/admin/care-order/detail', { id: orderPrimaryId })
  const parsed = parseCareOrderDetailResponse(raw)
  if (parsed) return parsed
  if (careOrderMutationSucceeded(raw)) {
    throw new Error('Order detail returned no data.')
  }
  const r = raw as { msg?: string }
  const msg = typeof r?.msg === 'string' && r.msg.trim() ? r.msg : 'Failed to load order detail'
  throw new Error(msg)
}

/** 列表/详情行上的备注（主单 `notes` 或跟进行 `followups[].notes`） */
export function resolveNotesFromCareOrderListRow(row: CareOrderListItem): string | undefined {
  const rowAny = row as unknown as Record<string, unknown>
  for (const key of ['notes', 'order_notes', 'order_note', 'remark', 'remarks'] as const) {
    const v = rowAny[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  for (const fu of row.followups ?? []) {
    const fuNotes = fu.notes?.trim()
    if (fuNotes) return fuNotes
    const fuAny = fu as Record<string, unknown>
    for (const key of ['notes', 'remark', 'remarks'] as const) {
      const v = fuAny[key]
      if (typeof v === 'string' && v.trim()) return v.trim()
    }
  }
  return undefined
}

function orderServiceLineIsFollowUp(line: OrderServiceLineDTO | undefined): boolean {
  return line?.serviceName.trim().toLowerCase() === 'follow-up care'
}

/** Add Notes：`order` 写主单；Follow-up 行写 `followup` 主键 */
export async function resolveCareOrderUpdateNotesTarget(
  order: OrderCardDTO,
  lineIndex: number,
  fetchDetail?: (orderPrimaryId: number) => Promise<CareOrderListItem>,
): Promise<{ target_type: 'order' | 'followup'; target_id: number } | null> {
  const line = order.services[lineIndex]
  if (orderServiceLineIsFollowUp(line)) {
    let fuId = line?.followUpPrimaryId
    const mainPk = resolveMainOrderPrimaryIdFromCard(order)
    if ((fuId == null || fuId <= 0) && mainPk != null && fetchDetail) {
      try {
        const row = await fetchDetail(mainPk)
        const fuBiz = (line?.productCode ?? '').trim()
        const hit =
          fuBiz && fuBiz !== '—'
            ? row.followups?.find((fu) => (fu.follow_up_id ?? '').trim() === fuBiz)
            : undefined
        const resolved = hit ?? row.followups?.find((fu) => resolveFollowUpRowPrimaryId(fu) != null)
        const pk = resolved ? resolveFollowUpRowPrimaryId(resolved) : undefined
        if (pk != null) fuId = pk
      } catch {
        /* 详情失败时仍尝试主单 target */
      }
    }
    if (fuId != null && fuId > 0) {
      return { target_type: 'followup', target_id: Math.trunc(fuId) }
    }
  }
  const pk = order.orderPrimaryId
  if (pk != null && Number.isFinite(pk) && pk > 0) {
    return { target_type: 'order', target_id: Math.trunc(pk) }
  }
  return null
}

/** POST `/api/admin/care-order/update-notes` 请求体（raw-json） */
export interface CareOrderUpdateNotesRequest {
  target_type: 'order' | 'followup'
  /** 主单为订单主键 `id`；Follow-up 为 follow-up 主键 */
  target_id: number
  notes: string
}

/** POST `/api/admin/care-order/update-notes`，更新订单备注 */
export async function postCareOrderUpdateNotes(body: CareOrderUpdateNotesRequest): Promise<void> {
  const raw = await request.post('/api/admin/care-order/update-notes', body)
  if (careOrderMutationSucceeded(raw)) return
  const r = raw as { msg?: string }
  const msg = typeof r?.msg === 'string' && r.msg.trim() ? r.msg : 'Failed to update notes'
  throw new Error(msg)
}

export type { CreateFollowUpRequest as CareOrderCreateFollowUpRequest } from './CreateFollowUp'
export { postCreateFollowUp as postCareOrderCreateFollowUp } from './CreateFollowUp'
export {
  postCareScheduleAssign,
  parseCareScheduleAssignResponse,
} from './CareScheduleAssign'
export { postCareScheduleUnassign } from './CareScheduleUnassign'
export type { CareScheduleUnassignRequest, CareScheduleUnassignResult } from './CareScheduleUnassign'
export type {
  CareScheduleAssignRequest,
  CareScheduleAssignTargetType,
  CareScheduleAssignResult,
} from './CareScheduleAssign'
export { postCareScheduleContext, parseCareScheduleContextResponse } from './CareScheduleContext'
export type {
  CareScheduleContextRequest,
  ParsedCareScheduleContext,
} from './CareScheduleContext'
export {
  postCareScheduleAvailability,
  parseCareScheduleAvailabilityResponse,
} from './CareScheduleAvailability'
export type {
  CareScheduleAvailabilityRequest,
  ParsedCareScheduleAvailability,
  CareScheduleDayAvailabilityTone,
} from './CareScheduleAvailability'

/** POST `/api/admin/care-order/delete`，body `{ id }` 为订单主键（int） */
export async function postCareOrderDelete(orderPrimaryId: number): Promise<void> {
  try {
    const raw = await request.post('/api/admin/care-order/delete', { id: orderPrimaryId })
    if (careOrderMutationSucceeded(raw)) return
    const r = raw as { msg?: string }
    const msg = typeof r?.msg === 'string' && r.msg.trim() ? r.msg : 'Failed to delete order'
    throw new Error(msg)
  } catch (e: unknown) {
    if (typeof e === 'object' && e !== null && 'response' in e) {
      const res = (e as { response?: { status?: number; data?: unknown } }).response
      if (res?.status === 404) {
        throw new Error(
          'Delete is not available on the server (care-order/delete returned 404). Ask backend to provide the delete API.',
        )
      }
      const d = res?.data
      if (typeof d === 'object' && d !== null && 'msg' in d) {
        const m = (d as { msg: unknown }).msg
        if (typeof m === 'string' && m.trim()) throw new Error(m)
      }
    }
    throw e
  }
}

/** 详情弹窗 checklist 行展示 */
export type CareOrderChecklistStatusTone =
  | 'attention'
  | 'normal'
  | 'major'
  | 'major-covered'
  | 'unable'
  | 'pending'

export interface CareOrderChecklistDisplayRow {
  title: string
  typeTag: 'Routine' | 'Baseline'
  /** API `type_text`，展示 Routine/Baseline 标签文案 */
  typeLabel?: string
  statusLabel: string
  statusTone: CareOrderChecklistStatusTone
  location: string
  /** 检查项主键，POST create-follow-up 的 order_item_id */
  orderItemId?: number
  /** 库 checklist 业务 ID（展示用，非 create-follow-up） */
  itemId?: string
  /** Major Issue - Covered Repair 跟进结果（正常 / 无法维修） */
  coveredRepairOutcome?: 'normal' | 'unable'
}

function splitInspectionProductTokens(code: string): string[] {
  const raw = (code ?? '').trim()
  if (!raw || raw === '—') return []
  if (/[·•,|]/.test(raw) || raw.includes('/')) {
    return raw.split(/\s*(?:[·•]|,|\||\/)\s*/).map((s) => s.trim()).filter(Boolean)
  }
  const compact = raw.replace(/\s+/g, ' ')
  if (/^(?:[A-Za-z]{1,3}-\d+\s+)+[A-Za-z]{1,3}-\d+$/i.test(compact)) {
    return compact.split(/\s+/).map((s) => s.trim()).filter(Boolean)
  }
  if (/^[A-Za-z]{1,3}-\d+$/i.test(raw)) return [raw]
  return []
}

function inspectionSummaryTokensFromCounts(c: InspectionCounts & { B?: number }): string[] {
  const order: (keyof InspectionCounts | 'B')[] = ['N', 'A', 'IC', 'I', 'U', 'B', 'P']
  const parts: string[] = []
  for (const k of order) {
    const n = k === 'B' ? (c.B ?? 0) : c[k as keyof InspectionCounts]
    if (n > 0) parts.push(k === 'IC' ? `IC-${n}` : `${k}-${n}`)
  }
  return parts
}

function parseInspectionSummaryTokens(raw: unknown): string[] | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const num = (v: unknown): number => {
    if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.trunc(v))
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) return Math.max(0, Math.trunc(n))
    }
    return 0
  }
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      if (k in o) {
        const x = num(o[k])
        if (x > 0) return x
      }
    }
    return 0
  }
  const c: InspectionCounts & { B?: number } = emptyInspectionCounts()
  c.P = pick('P', 'p', 'pending')
  c.N = pick('N', 'n', 'normal', 'completed_normal')
  c.A = pick('A', 'a', 'needs_attention')
  c.I = pick('I', 'i', 'major_issue')
  c.IC = pick('IC', 'ic', 'major_issue_covered')
  c.U = pick('U', 'u', 'unable')
  c.B = pick('B', 'b', 'baseline_pending', 'baseline')
  const tokens = inspectionSummaryTokensFromCounts(c)
  return tokens.length ? tokens : null
}

/** 详情弹窗顶部彩色码（N-10 · A-2 …） */
export function getCareOrderInspectionSummaryTokens(
  row: CareOrderListItem,
  fallbackProductCode?: string,
): string[] {
  const hasFollowUp = careOrderRowHasFollowUpOrder(row)
  const dropIc = (tokens: string[]) =>
    hasFollowUp ? tokens : tokens.filter((t) => !/^IC-\d+$/i.test(t.trim()))

  const fromSummary = parseInspectionSummaryTokens(row.inspection_status_summary)
  if (fromSummary?.length) return dropIc(fromSummary)
  const fromCode = splitInspectionProductTokens(
    fallbackProductCode?.trim() || buildMainLineInspectionProductCode(row),
  )
  if (fromCode.length) return dropIc(fromCode)
  const fromItems = aggregateInspectionCountsFromItems(row.items)
  if (fromItems && totalInspectionCounts(fromItems) > 0) {
    return dropIc(
      inspectionSummaryTokensFromCounts(applyInspectionIcFollowUpGate(fromItems, hasFollowUp)),
    )
  }
  return []
}

function inferChecklistLocation(item: CareOrderListItemChecklist): string {
  const r = item as Record<string, unknown>
  for (const k of ['location', 'zone', 'area', 'section', 'space']) {
    const v = r[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  const blob = checklistInspectionBlob(item)
  if (blob.includes('exterior') || blob.includes('室外') || blob.includes('户外')) return 'Exterior'
  if (blob.includes('interior') || blob.includes('室内')) return 'Interior'
  return 'Interior'
}

/** 解析详情 `data.tags` / 行级 tags 为 token 列表 */
export function parseCareOrderDetailTags(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    const out: string[] = []
    for (const entry of raw) {
      if (entry == null) continue
      if (typeof entry === 'string') {
        const s = entry.trim()
        if (s) out.push(...splitInspectionProductTokens(s))
        continue
      }
      if (typeof entry === 'object') {
        const o = entry as Record<string, unknown>
        const label = o.label ?? o.text ?? o.tag ?? o.value
        if (typeof label === 'string' && label.trim()) {
          out.push(...splitInspectionProductTokens(label.trim()))
        }
      }
    }
    return out.filter(Boolean)
  }
  if (typeof raw === 'string' && raw.trim()) {
    return splitInspectionProductTokens(raw)
  }
  if (raw != null && typeof raw === 'object') {
    const fromSummary = parseInspectionSummaryTokens(raw)
    if (fromSummary?.length) return fromSummary
    return pbTokensFromSummaryCountObject(raw as Record<string, unknown>)
  }
  return []
}

function stripBaselineInspectionTokens(tokens: string[]): string[] {
  return tokens.filter((t) => !/^B-\d+$/i.test(t.trim()))
}

function stripIcInspectionTokensWhenNoFollowUp(
  tokens: string[],
  hasFollowUp: boolean,
): string[] {
  if (hasFollowUp) return tokens
  return tokens.filter((t) => !/^IC-\d+$/i.test(t.trim()))
}

/** 订单卡片彩色码：与 Checklist and Follow-up 顶栏同源，但不展示 B */
export function careOrderCardInspectionTokensFromChecklistData(
  row: CareOrderListItem,
): string[] {
  return stripBaselineInspectionTokens(careOrderChecklistSummaryTokensFromDetail(row))
}

function readSummaryCount(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.trunc(raw))
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw.trim())
    if (Number.isFinite(n)) return Math.max(0, Math.trunc(n))
  }
  return 0
}

/** 从 { P: 2, B: 1 } 或 inspection_status_summary 提取 P-x / B-x */
function pbTokensFromSummaryCountObject(o: Record<string, unknown>): string[] {
  const tokens: string[] = []
  const b = readSummaryCount(o.B ?? o.b ?? o.baseline ?? o.baseline_count ?? o.baselineCount)
  const p = readSummaryCount(
    o.P ?? o.p ?? o.pending ?? o.pending_count ?? o.pendingCount,
  )
  if (b > 0) tokens.push(`B-${b}`)
  if (p > 0) tokens.push(`P-${p}`)
  return tokens
}

const CHECKLIST_DETAIL_SUMMARY_TOKEN_ORDER = ['N', 'A', 'IC', 'I', 'U', 'P', 'B'] as const

function inspectionTokenKind(token: string): string {
  const m = /^([A-Za-z]{1,3})-\d+$/i.exec(token.trim())
  return m?.[1]?.toUpperCase() ?? ''
}

/** Checklist 顶栏：N/I/U/P 等在前，B 始终排最后 */
function orderChecklistDetailSummaryTokens(tokens: string[]): string[] {
  const bTokens: string[] = []
  const other: string[] = []
  for (const token of tokens) {
    const t = token.trim()
    if (!t) continue
    if (/^B-\d+$/i.test(t)) bTokens.push(t)
    else other.push(t)
  }
  const rank = (kind: string) => {
    const idx = CHECKLIST_DETAIL_SUMMARY_TOKEN_ORDER.indexOf(
      kind as (typeof CHECKLIST_DETAIL_SUMMARY_TOKEN_ORDER)[number],
    )
    return idx >= 0 ? idx : CHECKLIST_DETAIL_SUMMARY_TOKEN_ORDER.length - 1
  }
  other.sort((a, b) => rank(inspectionTokenKind(a)) - rank(inspectionTokenKind(b)))
  return [...other, ...bTokens]
}

/** Checklist 顶栏汇总：B 排在 P 之后（最后） */
function checklistDetailSummaryTokensFromCounts(
  c: InspectionCounts & { B?: number },
): string[] {
  const parts: string[] = []
  for (const k of CHECKLIST_DETAIL_SUMMARY_TOKEN_ORDER) {
    const n = k === 'B' ? (c.B ?? 0) : c[k as keyof InspectionCounts]
    if (n > 0) parts.push(k === 'IC' ? `IC-${n}` : `${k}-${n}`)
  }
  return parts
}

function deriveChecklistSummaryTokensFromItems(
  items: CareOrderListItemChecklist[] | undefined,
): string[] {
  const fromItems = aggregateInspectionCountsFromChecklistDetailItems(items)
  if (!fromItems || totalInspectionCounts(fromItems) === 0) return []
  return checklistDetailSummaryTokensFromCounts(fromItems)
}

/** Checklist 顶栏：优先 items 汇总 N/A/I/U/P；B 排在最后 */
export function careOrderChecklistSummaryTokensFromDetail(
  row: CareOrderListItem | null | undefined,
): string[] {
  const baselineCount = countBaselineTaggedChecklistItems(row?.items)
  const finalize = (tokens: string[]) =>
    orderChecklistDetailSummaryTokens(appendBaselineSummaryToken(tokens, baselineCount))

  const hasFollowUp = row ? careOrderRowHasFollowUpOrder(row) : false

  if (row?.items?.length) {
    const fromItems = aggregateInspectionCountsFromChecklistDetailItems(row.items)
    if (fromItems && totalInspectionCounts(fromItems) > 0) {
      const tokens = finalize(
        checklistDetailSummaryTokensFromCounts(
          applyInspectionIcFollowUpGate(fromItems, hasFollowUp),
        ),
      )
      if (tokens.length) return tokens
    }
  }

  const fromTags = parseCareOrderDetailTags(row?.tags)
  if (fromTags.length) return finalize(fromTags)

  const fromDetailTags = parseCareOrderDetailTags(row?.detail_tags)
  if (fromDetailTags.length) return finalize(fromDetailTags)

  const fromSummary = parseCareOrderDetailTags(row?.checklist_summary)
  if (fromSummary.length) return finalize(fromSummary)

  if (row?.inspection_status_summary && typeof row.inspection_status_summary === 'object') {
    const fromInspection = parseInspectionSummaryTokens(row.inspection_status_summary)
    if (fromInspection?.length) return finalize(fromInspection)
  }

  return finalize(deriveChecklistSummaryTokensFromItems(row?.items))
}

function readChecklistStringField(item: CareOrderListItemChecklist, key: string): string {
  const v = (item as Record<string, unknown>)[key]
  return typeof v === 'string' ? v.trim() : ''
}

function isUncheckedChecklistItem(item: CareOrderListItemChecklist): boolean {
  const blob = normBlob(`${item.item_status ?? ''} ${item.status ?? ''}`)
  if (!blob) return true
  if (blob.includes('unchecked') || blob.includes('未检查')) return true
  if (blob.includes('pending care') || blob.includes('pending inspection')) return true
  return false
}

function typeTagFromTypeText(typeText: string): 'Routine' | 'Baseline' {
  const v = normBlob(typeText)
  if (v.includes('baseline')) return 'Baseline'
  return 'Routine'
}

function statusToneFromDisplayStatusLabel(label: string): CareOrderChecklistStatusTone {
  const st = normBlob(label)
  if (!st || st.includes('pending care') || st.includes('pending inspection')) return 'pending'
  if (st.includes('major issue') && (st.includes('covered repair') || st.includes('covered'))) {
    return 'major-covered'
  }
  if (st.includes('major issue') || st.includes('major')) return 'major'
  if (st.includes('unable to complete') || st.includes('unable to repair') || st.includes('unable')) {
    return 'unable'
  }
  if (st.includes('needs attention') || st.includes('attention')) return 'attention'
  if (st.includes('normal') || st.includes('completed')) return 'normal'
  return 'pending'
}

function checklistRowLocationFromApiFields(item: CareOrderListItemChecklist): string {
  const summaryRaw = item.checklist_summary
  const summary =
    typeof summaryRaw === 'string'
      ? summaryRaw.trim()
      : summaryRaw != null && typeof summaryRaw === 'object'
        ? String((summaryRaw as Record<string, unknown>).text ?? '').trim()
        : ''
  const tagTokens = parseCareOrderDetailTags(item.tags)
  const tagPart = tagTokens.length ? tagTokens.join(' · ') : ''
  if (summary && tagPart) return `${tagPart} · ${summary}`
  if (summary) return summary
  if (tagPart) return tagPart
  return inferChecklistLocation(item)
}

function inferChecklistTypeTag(item: CareOrderListItemChecklist): 'Routine' | 'Baseline' {
  const r = item as Record<string, unknown>
  for (const k of ['type', 'item_type', 'checklist_type', 'line_type', 'tag']) {
    const v = normBlob(String(r[k] ?? ''))
    if (v.includes('baseline')) return 'Baseline'
    if (v.includes('routine')) return 'Routine'
  }
  const blob = checklistInspectionBlob(item)
  if (blob.includes('baseline')) return 'Baseline'
  if (blob.includes('routine')) return 'Routine'
  const cat = normBlob(
    `${item.inspection_category ?? ''} ${item.category ?? ''} ${item.item_name ?? ''}`,
  )
  if (cat.includes('baseline')) return 'Baseline'
  return 'Routine'
}

/** 详情 Checklist：直接按 API items 映射（不走 N/I/U/B 桶合成） */
export function buildCareOrderChecklistRowsFromDetailItems(
  items: CareOrderListItemChecklist[] | undefined,
): CareOrderChecklistDisplayRow[] {
  if (!items?.length) return []
  const sorted = [...items].sort((a, b) => {
    const sa = typeof a.sort === 'number' ? a.sort : Number.MAX_SAFE_INTEGER
    const sb = typeof b.sort === 'number' ? b.sort : Number.MAX_SAFE_INTEGER
    return sa - sb
  })
  return sorted.map((item) => mapCareOrderChecklistDisplayRow(item))
}

/** 将 checklist 条目映射为详情弹窗行 */
export function mapCareOrderChecklistDisplayRow(
  item: CareOrderListItemChecklist,
): CareOrderChecklistDisplayRow {
  const typeText = readChecklistStringField(item, 'type_text')
  const typeTag = typeText ? typeTagFromTypeText(typeText) : inferChecklistTypeTag(item)
  const typeLabel = typeText || (typeTag === 'Baseline' ? 'Baseline' : 'Routine')

  const displayStatus = readChecklistStringField(item, 'display_status_text')
  if (displayStatus) {
    const statusLabel = displayStatus
    const statusTone = statusToneFromDisplayStatusLabel(statusLabel)
    const title =
      item.item_name?.trim() ||
      item.item_id?.trim() ||
      '—'
    let coveredRepairOutcome: 'normal' | 'unable' | undefined
    if (statusTone === 'major-covered') {
      coveredRepairOutcome = inferCoveredRepairFollowUpOutcomeFromBlob(normBlob(statusLabel))
    }
    return {
      title,
      typeTag,
      typeLabel,
      statusLabel,
      statusTone,
      location: checklistRowLocationFromApiFields(item),
      orderItemId: resolveChecklistOrderItemId(item),
      itemId: item.item_id?.trim() || undefined,
      ...(coveredRepairOutcome ? { coveredRepairOutcome } : {}),
    }
  }
  if (isUncheckedChecklistItem(item)) {
    const title =
      item.item_name?.trim() ||
      item.item_id?.trim() ||
      '—'
    return {
      title,
      typeTag,
      typeLabel,
      statusLabel: 'Pending Care',
      statusTone: 'pending',
      location: checklistRowLocationFromApiFields(item),
      orderItemId: resolveChecklistOrderItemId(item),
      itemId: item.item_id?.trim() || undefined,
    }
  }

  const st = checklistInspectionBlob(item)
  let statusLabel = 'Pending Care'
  let statusTone: CareOrderChecklistStatusTone = 'pending'
  let coveredRepairOutcome: 'normal' | 'unable' | undefined

  if (isRoutineMajorIssue(st) || st.includes('major issue')) {
    if (isMajorIssueCoveredRepairOrSubsequent(st) || st.includes('covered repair')) {
      statusLabel = 'Major Issue - Covered Repair'
      statusTone = 'major-covered'
      coveredRepairOutcome = inferCoveredRepairFollowUpOutcomeFromBlob(st)
    } else {
      statusLabel = 'Major Issue'
      statusTone = 'major'
    }
  } else if (isUnableToCompleteOrRepair(st)) {
    statusLabel = 'Unable to Complete'
    statusTone = 'unable'
  } else if (isRoutineNeedsAttention(st)) {
    statusLabel = 'Needs Attention'
    statusTone = 'attention'
  } else if (isRoutineNormalOrCompleted(st) || isBaselineCompleted(st)) {
    statusLabel = 'Normal'
    statusTone = 'normal'
  } else if (isPendingInspectionState(st)) {
    statusLabel = 'Pending Care'
    statusTone = 'pending'
  }

  const title =
    item.item_name?.trim() ||
    item.item_id?.trim() ||
    '—'

  return {
    title,
    typeTag,
    typeLabel,
    statusLabel,
    statusTone,
    location: checklistRowLocationFromApiFields(item),
    orderItemId: resolveChecklistOrderItemId(item),
    itemId: item.item_id?.trim() || undefined,
    ...(coveredRepairOutcome ? { coveredRepairOutcome } : {}),
  }
}

function inspectionBucketFromChecklistDisplayTone(
  tone: CareOrderChecklistStatusTone,
): keyof InspectionCounts | null {
  switch (tone) {
    case 'pending':
      return 'P'
    case 'normal':
      return 'N'
    case 'attention':
      return 'A'
    case 'major':
      return 'I'
    case 'major-covered':
      return 'IC'
    case 'unable':
      return 'U'
    default:
      return null
  }
}

/** 与 Checklist and Follow-up 列表行同源：items + display_status_text / type_text；Baseline 仅计入 B，其 Pending 不计入 P */
function aggregateInspectionCountsFromChecklistDetailItems(
  items: CareOrderListItemChecklist[] | undefined,
): (InspectionCounts & { B?: number }) | null {
  if (!items?.length) return null
  const c = emptyInspectionCounts()
  let baselineTotal = 0
  let any = false
  for (const it of items) {
    const row = mapCareOrderChecklistDisplayRow(it)
    if (row.typeTag === 'Baseline') {
      baselineTotal++
      any = true
      continue
    }
    const bucket = inspectionBucketFromChecklistDisplayTone(row.statusTone)
    if (bucket) {
      c[bucket] += 1
      any = true
    }
  }
  if (!any) return null
  return baselineTotal > 0 ? { ...c, B: baselineTotal } : c
}

function resolveChecklistOrderItemId(item: CareOrderListItemChecklist): number | undefined {
  if (typeof item.id === 'number' && Number.isFinite(item.id) && item.id > 0) {
    return Math.trunc(item.id)
  }
  const raw = item.item_id?.trim()
  if (raw && /^\d+$/.test(raw)) {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) return Math.trunc(n)
  }
  return undefined
}

/** create-follow-up 的 issue_level（与 Apipost 示例 major 对齐） */
export function issueLevelFromChecklistStatusTone(
  tone: CareOrderChecklistStatusTone,
): string {
  switch (tone) {
    case 'major-covered':
    case 'major':
      return 'major'
    case 'attention':
      return 'attention'
    case 'unable':
      return 'unable'
    default:
      return 'normal'
  }
}

/** 是否应对该检查项调用 create-follow-up */
export function canCreateFollowUpFromChecklistRow(row: CareOrderChecklistDisplayRow): boolean {
  if (row.orderItemId == null) return false
  return row.statusTone === 'major' || row.statusTone === 'major-covered' || row.statusTone === 'attention'
}

export function buildFollowUpNotesFromChecklistRow(row: CareOrderChecklistDisplayRow): string {
  const title = row.title.trim() || 'Inspection item'
  return `${title}: ${row.statusLabel.trim()}`
}

/** create-follow-up 业务 ID（与 Apipost 示例对齐，可由后端覆盖） */
export function buildFollowUpIdForCreate(
  orderDisplayId: string | undefined,
  row: CareOrderChecklistDisplayRow,
): string {
  const orderPart = (orderDisplayId ?? 'ORDER').trim().replace(/\s+/g, '') || 'ORDER'
  const itemPart =
    row.orderItemId != null
      ? String(row.orderItemId)
      : (row.itemId ?? 'item').trim().replace(/\s+/g, '') || 'item'
  return `FU-${orderPart}-${itemPart}`
}

/** 统计检查项中带 Baseline 标签的数量（与详情列表 Routine/Baseline 标签一致） */
export function countBaselineTaggedChecklistItems(
  items: CareOrderListItemChecklist[] | undefined,
): number {
  if (!items?.length) return 0
  let n = 0
  for (const it of items) {
    if (mapCareOrderChecklistDisplayRow(it).typeTag === 'Baseline') n++
  }
  return n
}

/** 在详情彩色码末尾（P 之前）插入 B-n */
export function appendBaselineSummaryToken(tokens: string[], baselineCount: number): string[] {
  if (baselineCount <= 0) return tokens
  const out = tokens.filter((t) => !/^B-\d+$/i.test(t.trim()))
  const bToken = `B-${baselineCount}`
  const pIdx = out.findIndex((t) => /^P-/i.test(t.trim()))
  if (pIdx >= 0) {
    out.splice(pIdx, 0, bToken)
    return out
  }
  return [...out, bToken]
}

function parseInspectionTokenCount(token: string): { kind: string; count: number } | null {
  const m = /^([A-Za-z]{1,3})-(\d+)$/i.exec(token.trim())
  if (!m) return null
  if (!m?.[1] || m[2] == null) return null
  const count = Number(m[2])
  if (!Number.isFinite(count) || count <= 0) return null
  return { kind: m[1].toUpperCase(), count: Math.trunc(count) }
}

const CHECKLIST_NAME_POOL: Record<string, string[]> = {
  N: [
    'Water pipe valve closure check',
    'Exterior door weather seal check',
    'Smoke detector battery test',
    'Garage door safety sensor test',
    'Attic ventilation inspection',
    'Foundation drainage check',
  ],
  A: [
    'Gypsum cornice crack repair',
    'Window frame caulking review',
    'Deck railing stability check',
    'Interior paint touch-up review',
  ],
  IC: [
    'Water pipe valve closure inspection',
    'HVAC duct leak inspection',
    'Roof flashing inspection',
  ],
  I: [
    'Courtyard door screen inspection',
    'Electrical panel inspection',
    'Sump pump operation test',
  ],
  U: [
    'Condensing Unit check',
    'Attic access inspection',
    'Crawl space moisture reading',
  ],
  B: [
    'Roof Covering – Material & Age',
    'HVAC system baseline',
    'Water heater baseline',
  ],
  P: [
    'Pre-service walkthrough',
    'System readiness check',
    'Exterior envelope scan',
  ],
  F: ['Follow-up repair verification', 'Follow-up inspection item'],
}

function hashOrderSeed(orderId?: string): number {
  const s = (orderId ?? '').trim()
  if (!s) return 0
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h
}

function groupChecklistItemsBySummaryBucket(
  items: CareOrderListItemChecklist[],
): Record<string, CareOrderListItemChecklist[]> {
  const sorted = [...items].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
  const buckets: Record<string, CareOrderListItemChecklist[]> = {
    P: [],
    N: [],
    A: [],
    I: [],
    IC: [],
    U: [],
    B: [],
    F: [],
  }
  for (const it of sorted) {
    if (mapCareOrderChecklistDisplayRow(it).typeTag === 'Baseline') {
      buckets.B!.push(it)
      continue
    }
    const k = classifyChecklistToBucket(it)
    if (k) buckets[k]!.push(it)
  }
  return buckets
}

function fallbackDisplayRowForBucket(
  kind: string,
  index: number,
  orderId?: string,
): CareOrderChecklistDisplayRow {
  const pool = CHECKLIST_NAME_POOL[kind] ?? CHECKLIST_NAME_POOL.P ?? ['Inspection item']
  const seed = (hashOrderSeed(orderId) + index * 17) >>> 0
  const title = pool[(seed + index) % pool.length] ?? 'Inspection item'
  const locations = ['Interior', 'Exterior']
  const location = locations[seed % locations.length] ?? 'Interior'

  switch (kind) {
    case 'P':
      return {
        title,
        typeTag: index % 2 === 0 ? 'Routine' : 'Baseline',
        statusLabel: 'Pending Care',
        statusTone: 'pending',
        location,
      }
    case 'N':
      return {
        title,
        typeTag: index % 3 === 0 ? 'Baseline' : 'Routine',
        statusLabel: 'Normal',
        statusTone: 'normal',
        location,
      }
    case 'A':
      return {
        title,
        typeTag: 'Routine',
        statusLabel: 'Needs Attention',
        statusTone: 'attention',
        location,
      }
    case 'IC':
      return {
        title,
        typeTag: 'Routine',
        statusLabel: 'Major Issue - Covered Repair',
        statusTone: 'major-covered',
        coveredRepairOutcome: 'normal',
        location,
      }
    case 'I':
      return {
        title,
        typeTag: 'Routine',
        statusLabel: 'Major Issue',
        statusTone: 'major',
        location,
      }
    case 'U':
      return {
        title,
        typeTag: index % 2 === 0 ? 'Routine' : 'Baseline',
        statusLabel: 'Unable to Complete',
        statusTone: 'unable',
        location,
      }
    case 'B': {
      const bPool = CHECKLIST_NAME_POOL.B ?? ['Baseline inspection item']
      return {
        title: bPool[(seed + index) % bPool.length] ?? title,
        typeTag: 'Baseline',
        statusLabel: 'Pending Care',
        statusTone: 'pending',
        location,
      }
    }
    case 'F':
      return {
        title,
        typeTag: 'Routine',
        statusLabel: 'Pending Care',
        statusTone: 'pending',
        location,
      }
    default:
      return {
        title,
        typeTag: 'Routine',
        statusLabel: 'Pending Care',
        statusTone: 'pending',
        location,
      }
  }
}

function takeChecklistRowsForBucket(
  kind: string,
  count: number,
  bucketItems: CareOrderListItemChecklist[] | undefined,
  orderId?: string,
): CareOrderChecklistDisplayRow[] {
  const rows: CareOrderChecklistDisplayRow[] = []
  const pool = bucketItems ?? []
  for (let i = 0; i < count; i++) {
    const item = pool[i]
    if (item) {
      const row = mapCareOrderChecklistDisplayRow(item)
      if (kind === 'B') row.typeTag = 'Baseline'
      rows.push(row)
    } else {
      rows.push(fallbackDisplayRowForBucket(kind, i, orderId))
    }
  }
  return rows
}

/** 按订单卡片彩色码（如 I-3）生成详情 checklist：条数与码后数字一致 */
export function buildCareOrderChecklistRowsFromSummaryTokens(
  tokens: string[],
  items?: CareOrderListItemChecklist[],
  orderId?: string,
): CareOrderChecklistDisplayRow[] {
  const countByKind = new Map<string, number>()
  for (const token of tokens) {
    const p = parseInspectionTokenCount(token)
    if (p) countByKind.set(p.kind, (countByKind.get(p.kind) ?? 0) + p.count)
  }
  if (!countByKind.size) return []

  const buckets = items?.length ? groupChecklistItemsBySummaryBucket(items) : undefined
  const displayOrder = ['A', 'N', 'IC', 'I', 'U', 'B', 'P', 'F']
  const rows: CareOrderChecklistDisplayRow[] = []

  for (const kind of displayOrder) {
    let count = countByKind.get(kind)
    if (!count) continue
    if (kind === 'B') {
      const bLen = buckets?.B?.length ?? 0
      if (bLen <= 0) continue
      count = bLen
    }
    rows.push(...takeChecklistRowsForBucket(kind, count, buckets?.[kind], orderId))
  }

  return rows
}

/** 无 API checklist 时，按主行 productCode 彩色码生成展示行 */
export function buildCareOrderChecklistFallbackRows(
  productCode: string,
  orderId?: string,
): CareOrderChecklistDisplayRow[] {
  return buildCareOrderChecklistRowsFromSummaryTokens(
    splitInspectionProductTokens(productCode),
    undefined,
    orderId,
  )
}

/** POST `/api/admin/care-order/list`，body 为 snake_case JSON */
export async function postCareOrderList(params: OrderListQuery): Promise<unknown> {
  const body: Record<string, unknown> = {
    page: params.page,
    page_size: clampPageSize(params.pageSize),
  }
  const kw = params.searchQuery?.trim()
  if (kw) {
    body.search_type = params.searchField
    body.keyword = kw
  }
  if (params.serviceDate) body.service_date = params.serviceDate
  if (params.followUpDate) body.follow_up_date = params.followUpDate
  if (params.serviceStatus) body.service_status = params.serviceStatus
  if (params.technicianId && params.technicianId !== 'All') {
    const n = Number(params.technicianId)
    if (!Number.isNaN(n)) body.technician_id = n
  }
  if (params.inspectorId && params.inspectorId !== 'All') {
    const n = Number(params.inspectorId)
    if (!Number.isNaN(n)) body.inspector_id = n
  }
  return request.post('/api/admin/care-order/list', body)
}

/** @deprecated 使用 {@link postCareOrderList} */
export async function getOrdersApi(params: OrderListQuery): Promise<unknown> {
  return postCareOrderList(params)
}

export async function getTechniciansApi(): Promise<unknown> {
  return request.get('/api/care/technicians')
}

/** 订单行操作：后端路径约定；失败时由页面 Toast + 不刷新列表 */
export async function postCareOrderLineAction(
  orderId: string,
  lineIndex: number,
  action: 'schedule' | 'refund' | 'message' | 'note',
  body?: Record<string, unknown>,
): Promise<unknown> {
  return request.post(
    `/api/care/orders/${encodeURIComponent(orderId)}/lines/${lineIndex}/${action}`,
    body ?? {},
  )
}

/** 后端 MOCK-ORD-002 列表行（Assigned / Enhanced Care / Sam Taylor） */
export const MOCK_CARE_ORDER_LIST_ITEM_002: CareOrderListItem = {
  id: 9002,
  order_id: 'MOCK-ORD-002',
  home_id: 'MOCK-HOME-002',
  user_id: 'MOCK-USER-001',
  subscription_id: 'MOCK-SUB-002',
  report_id: '',
  plan_key: 'enhanced_care',
  service_name: 'Enhanced Care',
  care_template_id: 9002,
  service_status: 'assigned',
  service_status_text: 'Assigned',
  service_date: '2026-05-25',
  service_time_start: '13:30:00',
  service_time_end: '16:30:00',
  service_time_text: '2026-05-25 01:30PM - 04:30PM',
  technician_id: 9003,
  technician: {
    id: 9003,
    name: 'Sam Taylor',
    staff_no: 'T0009003',
    role: 'technician',
  },
  assignment_text: 'Sam Taylor',
  service_address: '606 Harbour View Rd Unit 1608, Vancouver, BC V6B 2X1',
  contact_name: 'Ava Chen',
  contact_phone: '+1 647-111-2222',
  subtotal_amount: 260,
  tax_rate: 0.13,
  tax_amount: 33.8,
  total_amount: 293.8,
  total_amount_text: '$293.80',
  payment_method: 'Credit Card',
  notes: 'Concierge requires technician ID at arrival.',
  placed_at: '2026-05-21 08:49:23',
}

/** MOCK-ORD-002 转为列表卡片 DTO（与 care-order/list 解析一致） */
export function mockCareOrderCard002(): OrderCardDTO {
  return careOrderListItemToCardDto(MOCK_CARE_ORDER_LIST_ITEM_002)
}
